import { useState, useEffect, useRef, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Container, Typography, Grid, Card, CardContent, Button, Divider, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { WaterDrop, CalendarToday, LocationOn, Thermostat, Speed, Assessment, Timeline, Circle, AcUnit, PhotoCamera, ArrowBackIos, ArrowForwardIos, ErrorOutline, Download, Home as HomeIcon, Fullscreen, Close } from '@mui/icons-material';
import { API_URL } from '../config/api';
import { ClientConfig, GlacierAssignment, loadClientConfig } from '../config/clientConfiguration';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Chart from 'chart.js/auto';
import ProfessionalExecutiveReport from '../components/ProfessionalExecutiveReport';
import AuthenticatedImage from '../components/AuthenticatedImage';
import DashboardLoadingScreen from '../components/DashboardLoadingScreen';
import {
  averageMonthlyGrowthPerStupa,
  formatHarvestedFreezingTime,
  formatGlacierTotalVolume,
  formatLast30DaysVolume,
  maxAvailableHarvestedHours,
} from '../utils/reviewHelpers';
import { ExecutiveReportModel } from '../utils/executiveReportModel';

interface ClientPageProps {
  sponsor: string;
  preview?: boolean;
}

type SponsorPresentationConfig = ClientConfig & {
  stupaIds: GlacierAssignment[];
};

interface ClientDashboardProps {
  sponsorSlug: string;
  sponsorConfig: SponsorPresentationConfig;
  preview?: boolean;
}

const galleryImageKey = (image: any) =>
  `${String(image?.cam_id || '')}:${String(image?.file_name || '')}`;

const ClientDashboard = ({ sponsorSlug, sponsorConfig, preview = false }: ClientDashboardProps) => {

  // Real data states
  const [stupaData, setStupaData] = useState<any>(null);
  const [waterReplenishment, setWaterReplenishment] = useState<any>(null);
  const [cameraImages, setCameraImages] = useState<any[]>([]);
  const [cameraStatuses, setCameraStatuses] = useState<any[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState(sponsorConfig.cameras[0] || '');
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  
  // Progressive loading states
  const [loadingSteps, setLoadingSteps] = useState({
    connecting: true,
    stupaData: false,
    images: false,
    complete: false
  });
  const [loadingMessage, setLoadingMessage] = useState('Establishing secure connection...');
  
  // UI states
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(false);
  
  // New states for enhanced functionality
  const [cameraGalleryOpen, setCameraGalleryOpen] = useState(false);
  const [fullscreenImageOpen, setFullscreenImageOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [executiveReportOpen, setExecutiveReportOpen] = useState(false);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [spreadsheetGenerating, setSpreadsheetGenerating] = useState(false);
  const [executiveReportModel, setExecutiveReportModel] =
    useState<ExecutiveReportModel | null>(null);
  const [reportExportError, setReportExportError] = useState<string | null>(null);
  const [logoError, setLogoError] = useState(false);
  
  // Enhanced gallery states
  const [selectedDate, setSelectedDate] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [galleryImages, setGalleryImages] = useState<any[]>([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryPage, setGalleryPage] = useState(1);
  const [galleryTotal, setGalleryTotal] = useState(0);
  const [loadedGalleryImages, setLoadedGalleryImages] = useState<Set<string>>(
    new Set()
  );
  
  // Weekly navigation states
  const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
  const [weeklyImages, setWeeklyImages] = useState<any[]>([]);
  const [availableWeeks, setAvailableWeeks] = useState<string[]>([]);
  
  // Report generation states
  const reportRef = useRef<HTMLDivElement>(null);
  const reportReadyRef = useRef(false);
  
  // Date slider states for historical data viewing
  const [currentViewDate, setCurrentViewDate] = useState<Date>(new Date());
  const [stupaDeploymentDate] = useState<Date>(
    new Date(sponsorConfig.seasonStart || '2026-04-27')
  );
  const [maxViewDate] = useState<Date>(new Date()); // Today
  
  // Historical data states
  const [historicalStupaData, setHistoricalStupaData] = useState<any>(null);
  const [historicalWaterData, setHistoricalWaterData] = useState<any>(null);
  const [historicalCameraImages, setHistoricalCameraImages] = useState<any[]>([]);
  const [isLoadingHistorical, setIsLoadingHistorical] = useState(false);
  
  // Debouncing state for slider
  const [sliderDebounceTimer, setSliderDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  
  // Add state for image loading when time changes
  const [isImageLoadingForTime, setIsImageLoadingForTime] = useState(false);
  
  // Add state for initial image loading
  const [hasInitialImageLoaded, setHasInitialImageLoaded] = useState(false);

  // Add state for original progress (locked to page load)
  const [originalTotalReplenished, setOriginalTotalReplenished] = useState<number>(0);
  const dataTimestamp = sponsorConfig.replenishmentDataThrough
    ? new Date(`${sponsorConfig.replenishmentDataThrough}T00:00:00`)
    : null;
  const originalDataTimestamp = dataTimestamp;

  // Add state for selected stupa
  const [selectedStupa, setSelectedStupa] = useState(
    sponsorConfig.stupaIds.length > 1 
      ? { id: 'home', name: 'All Stupas' }
      : sponsorConfig.stupaIds[0]
  ); // Default to home view if multiple stupas, otherwise first stupa
  const [stupaDataMap, setStupaDataMap] = useState<{[key: string]: any}>({});
  const [waterDataMap, setWaterDataMap] = useState<{[key: string]: any}>({});
  const previewBase = `${API_URL}/admin/commercial/customers/${encodeURIComponent(sponsorSlug)}`;
  const companyApiBase = preview ? previewBase : `${API_URL}/client/me`;
  const logoIsProtected = sponsorConfig.logo.startsWith('/Volumes/');
  const logoUrl = logoIsProtected ? `${companyApiBase}/logo` : sponsorConfig.logo;
  const selectedCameraStatus = cameraStatuses.find(
    (item) => item?.cam_id === selectedCameraId,
  );
  const cameraOfflineWarning = selectedCameraStatus?.status === 'NO DATA';
  const stupaApiUrl = useCallback(
    (stupaId: string, resource: string) => preview
      ? `${previewBase}/stupa/${encodeURIComponent(stupaId)}/${resource}`
      : `${API_URL}/client/stupa/${encodeURIComponent(stupaId)}/${resource}`,
    [preview, previewBase],
  );

  const companyImagesUrl = useCallback((
    date?: string,
    limit = 8,
    page = 1,
    cameraId = selectedCameraId,
  ) => {
    const params = new URLSearchParams({
      limit: String(limit),
      page: String(page),
    });
    if (cameraId) params.set('cam_id', cameraId);
    if (date) params.set('date', date);
    return `${companyApiBase}/images?${params.toString()}`;
  }, [companyApiBase, selectedCameraId]);

  // Function to handle stupa switching
  const handleStupaSwitch = async (stupa: typeof sponsorConfig.stupaIds[0]) => {
    setSelectedStupa(stupa);

    const cachedStupa = stupaDataMap[stupa.id];
    const cachedWater = waterDataMap[stupa.id];
    if (cachedStupa && cachedWater?.monthly_replenished) {
      setStupaData(cachedStupa);
      setWaterReplenishment(cachedWater);
      setHistoricalStupaData(null);
      setHistoricalWaterData(null);
      setCurrentViewDate(new Date());
      return;
    }

    // Reset loading state for the new stupa
    setLoading(true);
    setLoadingSteps({
      connecting: true,
      stupaData: false,
      images: false,
      complete: false
    });
    setLoadingMessage('Connecting to monitoring station...');
    
    try {
      setLoadingMessage('Loading stupa monitoring data...');
      setLoadingSteps(prev => ({ ...prev, connecting: false, stupaData: true }));
      
      // Step 2: Fetch ONLY real stupa data - NO FALLBACKS
      const dashboardResponse = await authenticatedFetch(
        stupaApiUrl(stupa.id, 'dashboard-fast')
      );
      
      if (!dashboardResponse.ok) {
        throw new Error(`Failed to fetch data for ${stupa.id}: ${dashboardResponse.status}`);
      }
      
      const dashboardData = await dashboardResponse.json();
      
      // Step 3: Update monitoring data and complete. Camera metadata has an
      // independent fetch path so camera changes never reload this dashboard.
      setLoadingMessage('Dashboard ready!');
      setLoadingSteps(prev => ({
        ...prev,
        stupaData: false,
        images: false,
        complete: true,
      }));
      
      // Update the cached data maps with REAL data
      setStupaDataMap(prev => ({ ...prev, [stupa.id]: dashboardData }));
      setWaterDataMap(prev => ({ 
        ...prev, 
        [stupa.id]: {
          monthly_replenished: dashboardData.replenishment_stats.monthly_replenished,
          total_replenished: dashboardData.replenishment_stats.total_replenished,
          current_month_replenished: dashboardData.replenishment_stats.current_month_replenished,
          last_30_days_replenished: dashboardData.replenishment_stats.last_30_days_replenished,
          average_monthly_replenished: dashboardData.replenishment_stats.average_monthly_replenished,
          average_month_start: dashboardData.replenishment_stats.average_month_start,
          average_month_end: dashboardData.replenishment_stats.average_month_end,
        }
      }));
      // Update the main displayed data
      setStupaData(dashboardData);
      setWaterReplenishment({
        monthly_replenished: dashboardData.replenishment_stats.monthly_replenished,
        total_replenished: dashboardData.replenishment_stats.total_replenished,
        current_month_replenished: dashboardData.replenishment_stats.current_month_replenished,
        last_30_days_replenished: dashboardData.replenishment_stats.last_30_days_replenished,
        average_monthly_replenished: dashboardData.replenishment_stats.average_monthly_replenished,
        average_month_start: dashboardData.replenishment_stats.average_month_start,
        average_month_end: dashboardData.replenishment_stats.average_month_end,
      });
      // Reset historical data when switching stupas
      setHistoricalStupaData(null);
      setHistoricalWaterData(null);
      setHistoricalCameraImages([]);
      
      // Reset to current date when switching stupas
      setCurrentViewDate(new Date());
      
    } catch (error) {
      console.error('Error switching stupa:', error);
      setError(`Failed to load data for ${stupa.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Ensure data is loaded on component mount and when stupa changes
  useEffect(() => {
    if (selectedStupa && selectedStupa.id !== 'home') {
      if (waterDataMap[selectedStupa.id]?.monthly_replenished) {
        setStupaData(stupaDataMap[selectedStupa.id]);
        setWaterReplenishment(waterDataMap[selectedStupa.id]);
      }
    }
  }, [selectedStupa.id, waterDataMap, stupaDataMap]); // Run when selected stupa or data maps change

  // Fetch real data from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Connect and request each independent dashboard payload in parallel.
        setLoadingMessage('Connecting to monitoring station...');
        setLoadingSteps(prev => ({ ...prev, connecting: true, stupaData: false }));

        setLoadingMessage('Loading stupa monitoring data...');
        setLoadingSteps(prev => ({ ...prev, connecting: false, stupaData: true }));

        const stupaDataResults: {[key: string]: any} = {};
        const waterDataResults: {[key: string]: any} = {};
        let totalReplenishedAcrossAllStupas = 0;

        const summaryResponse = await authenticatedFetch(
          `${companyApiBase}/dashboard-summary`
        );
        if (!summaryResponse.ok) {
          throw new Error(
            `Failed to fetch dashboard summary: ${summaryResponse.status}`
          );
        }
        const summary = await summaryResponse.json();
        const dashboardEntries = sponsorConfig.stupaIds.map((stupa) => {
          const dashboardData = summary.glaciers?.[stupa.id];
          if (!dashboardData) {
            throw new Error(`Dashboard summary is missing ${stupa.id}`);
          }
          return { stupa, dashboardData };
        });

        dashboardEntries.forEach(({ stupa, dashboardData }) => {
          stupaDataResults[stupa.id] = dashboardData;
          const waterData = {
            monthly_replenished: dashboardData.replenishment_stats.monthly_replenished,
            total_replenished: dashboardData.replenishment_stats.total_replenished,
            current_month_replenished: dashboardData.replenishment_stats.current_month_replenished,
            last_30_days_replenished: dashboardData.replenishment_stats.last_30_days_replenished,
            average_monthly_replenished: dashboardData.replenishment_stats.average_monthly_replenished,
            average_month_start: dashboardData.replenishment_stats.average_month_start,
            average_month_end: dashboardData.replenishment_stats.average_month_end,
          };
          waterDataResults[stupa.id] = waterData;

          // The backend already applies the authenticated company's ownership share.
          totalReplenishedAcrossAllStupas += dashboardData.replenishment_stats.total_replenished;
        });
        
        // Set all the data maps
        setStupaDataMap(stupaDataResults);
        setWaterDataMap(waterDataResults);
        
        if (sponsorConfig.stupaIds.length > 1) {
          // For home view, use aggregated data from all stupas
          const cumulativeStats = {
            total_replenished: totalReplenishedAcrossAllStupas,
            total_managed: Object.values(stupaDataResults).reduce((sum: number, stupa: any) => sum + (stupa.replenishment_stats?.total_managed || 0), 0),
            monthly_replenished: [0, 0, 0, 0, 0, 0], // Will be calculated by getCumulativeStats
            current_month_replenished: 0, // Will be calculated by getCumulativeStats
            last_30_days_replenished: 0, // Will be calculated by getCumulativeStats
            average_monthly_replenished: Object.values(waterDataResults)
              .reduce((sum: number, item: any) =>
                sum + Number(item.average_monthly_replenished || 0), 0),
          };
          setStupaData({ replenishment_stats: cumulativeStats });
          setWaterReplenishment(cumulativeStats);
        } else {
          const onlyStupa = sponsorConfig.stupaIds[0];
          setStupaData(stupaDataResults[onlyStupa.id]);
          setWaterReplenishment(waterDataResults[onlyStupa.id]);
        }
        
        // Store original total across all stupas for progress bar
        setOriginalTotalReplenished(totalReplenishedAcrossAllStupas);
        
        setLoadingMessage('Dashboard ready!');
        setLoadingSteps(prev => ({
          ...prev,
          images: false,
          complete: true,
        }));
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(`Failed to load monitoring data: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setLoadingMessage('Connection failed - check system status');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    companyApiBase,
    sponsorConfig.stupaIds,
    stupaApiUrl,
  ]);

  useEffect(() => {
    if (loading) return;

    setCurrentImageIndex(0);
    setImageError(false);
    setImageLoading(true);
    setHasInitialImageLoaded(false);

    let active = true;
    const loadSelectedCamera = async () => {
      if (!selectedCameraId) {
        setCameraImages([]);
        setCameraStatuses([]);
        setWeeklyImages([]);
        setImageLoading(false);
        setHasInitialImageLoaded(true);
        return;
      }
      try {
        const response = await authenticatedFetch(
          companyImagesUrl(undefined, 1, 1, selectedCameraId)
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch camera metadata: ${response.status}`);
        }
        const data = await response.json();
        if (!active) return;
        const images = data.images || [];
        setCameraImages(images);
        setCameraStatuses(data.camera_statuses || []);
        setWeeklyImages(images);
        setCurrentWeekIndex(0);
        if (images.length === 0) {
          setImageLoading(false);
          setHasInitialImageLoaded(true);
        }
      } catch (cameraError) {
        if (!active) return;
        console.error('Error fetching selected camera:', cameraError);
        setCameraImages([]);
        setCameraStatuses([]);
        setWeeklyImages([]);
        setImageError(true);
        setImageLoading(false);
        setHasInitialImageLoaded(true);
      }
    };

    loadSelectedCamera();
    return () => {
      active = false;
    };
  }, [companyImagesUrl, loading, selectedCameraId]);

  // Fetch historical data when date changes (with debouncing)
  useEffect(() => {
    // Clear existing timer
    if (sliderDebounceTimer) {
      clearTimeout(sliderDebounceTimer);
    }
    
    const today = new Date().toDateString();
    const selectedDay = currentViewDate.toDateString();
    
    // If viewing today, reset to current data immediately
    if (selectedDay === today) {
      setHistoricalStupaData(null);
      setHistoricalWaterData(null);
      setHistoricalCameraImages([]);
      setIsImageLoadingForTime(false);
      return;
    }
    
    // Set loading state immediately when date changes
    setIsImageLoadingForTime(true);
    
    // Set up debounced data fetching (wait 800ms after user stops moving slider)
    const newTimer = setTimeout(() => {
      fetchHistoricalData(currentViewDate);
    }, 800); // Wait 800ms after user stops moving slider
    
    setSliderDebounceTimer(newTimer);
    
    // Cleanup function
    return () => {
      if (newTimer) {
        clearTimeout(newTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewDate]);

  // Navigation functions for image browsing
  const nextImage = () => {
    if (weeklyImages.length > 0) {
      setImageLoading(true);
      setCurrentWeekIndex((prev) => (prev + 1) % weeklyImages.length);
      // Update current image index to show the weekly image
      const weeklyImage = weeklyImages[(currentWeekIndex + 1) % weeklyImages.length];
      if (weeklyImage) {
        const imageIndex = cameraImages.findIndex(img => img.file_name === weeklyImage.file_name);
        if (imageIndex !== -1) {
          setCurrentImageIndex(imageIndex);
        }
      }
    }
  };

  const prevImage = () => {
    if (weeklyImages.length > 0) {
      setImageLoading(true);
      setCurrentWeekIndex((prev) => (prev - 1 + weeklyImages.length) % weeklyImages.length);
      // Update current image index to show the weekly image
      const weeklyImage = weeklyImages[(currentWeekIndex - 1 + weeklyImages.length) % weeklyImages.length];
      if (weeklyImage) {
        const imageIndex = cameraImages.findIndex(img => img.file_name === weeklyImage.file_name);
        if (imageIndex !== -1) {
          setCurrentImageIndex(imageIndex);
        }
      }
    }
  };
  
  // Function to get weekly images (one per week at 12:00 PM)
  const generateWeeklyImages = (images: any[]) => {
    if (!images || images.length === 0) return [];
    
    const weeklyImages: any[] = [];
    
    // Group images by week
    const imagesByWeek: { [weekKey: string]: any[] } = {};
    
    images.forEach(image => {
      const imageDate = new Date(image.timestamp);
      const weekStart = new Date(imageDate);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay()); // Start of week (Sunday)
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!imagesByWeek[weekKey]) {
        imagesByWeek[weekKey] = [];
      }
      imagesByWeek[weekKey].push(image);
    });
    
    // For each week, find the image closest to 12:00 PM
    Object.keys(imagesByWeek).sort().forEach(weekKey => {
      const weekImages = imagesByWeek[weekKey];
      if (weekImages.length > 0) {
        // Find image closest to 12:00 PM
        let bestImage = weekImages[0];
        let bestTimeDiff = Math.abs(new Date(bestImage.timestamp).getHours() - 12);
        
        weekImages.forEach(image => {
          const imageHour = new Date(image.timestamp).getHours();
          const timeDiff = Math.abs(imageHour - 12);
          if (timeDiff < bestTimeDiff) {
            bestTimeDiff = timeDiff;
            bestImage = image;
          }
        });
        
        weeklyImages.push(bestImage);
      }
    });
    
    return weeklyImages;
  };
  
  // The backend returns only cameras authorized for the active company.
  const loadGalleryForCamera = async (
    cameraId: string,
    page = 1,
    date?: string,
  ) => {
    setGalleryLoading(true);
    try {
      const response = await authenticatedFetch(
        companyImagesUrl(date, 8, page, cameraId)
      );
      if (response.ok) {
        const data = await response.json();
        const images = data.images || [];
        setGalleryImages(images.sort((a: any, b: any) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        ));
        setGalleryPage(Number(data.page || page));
        setGalleryTotal(Number(data.total || 0));
        setLoadedGalleryImages(
          new Set(images[0] ? [galleryImageKey(images[0])] : [])
        );
      } else {
        setGalleryImages([]);
        setGalleryTotal(0);
      }
    } catch (error: any) {
      console.error('Error loading camera gallery:', error);
      setGalleryImages([]);
      setGalleryTotal(0);
    } finally {
      setGalleryLoading(false);
    }
  };

  // New helper functions
  const openCameraGallery = async () => {
    setSelectedImageIndex(currentImageIndex);
    setCameraGalleryOpen(true);
    setSelectedDate('');
    await loadGalleryForCamera(selectedCameraId, 1);
  };
  
  const loadImagesForDate = async (date: string) => {
    await loadGalleryForCamera(selectedCameraId, 1, date);
  };
  
  const handleDateChange = (newDate: string) => {
    setSelectedDate(newDate);
    loadImagesForDate(newDate);
  };
  
  // Helper function to create charts for PDF
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const createChart = (data: number[], labels: string[], title: string, type: 'bar' | 'line' = 'bar'): Promise<string> => {
    return new Promise((resolve) => {
      // Create a canvas element
      const canvas = document.createElement('canvas');
      canvas.width = 800;
      canvas.height = 400;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        resolve('');
        return;
      }
      
      // Create the chart
      const chart = new Chart(ctx, {
        type: type,
        data: {
          labels: labels,
          datasets: [{
            label: 'Water Replenished (m³)',
            data: data,
            backgroundColor: type === 'bar' ? [
              'rgba(14, 165, 233, 0.8)',
              'rgba(59, 130, 246, 0.8)',
              'rgba(99, 102, 241, 0.8)',
              'rgba(139, 92, 246, 0.8)',
              'rgba(168, 85, 247, 0.8)',
              'rgba(236, 72, 153, 0.8)'
            ] : 'rgba(14, 165, 233, 0.8)',
            borderColor: type === 'bar' ? [
              'rgba(14, 165, 233, 1)',
              'rgba(59, 130, 246, 1)',
              'rgba(99, 102, 241, 1)',
              'rgba(139, 92, 246, 1)',
              'rgba(168, 85, 247, 1)',
              'rgba(236, 72, 153, 1)'
            ] : 'rgba(14, 165, 233, 1)',
            borderWidth: 2,
            fill: type === 'line' ? false : true
          }]
        },
        options: {
          responsive: false,
          animation: false,
          plugins: {
            title: {
              display: true,
              text: title,
              font: {
                size: 18,
                weight: 'bold'
              },
              color: '#1f2937'
            },
            legend: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Water Volume (m³)',
                font: {
                  size: 14,
                  weight: 'bold'
                }
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.1)'
              }
            },
            x: {
              title: {
                display: true,
                text: 'Week',
                font: {
                  size: 14,
                  weight: 'bold'
                }
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.1)'
              }
            }
          },
          elements: {
            bar: {
              borderRadius: 4
            }
          }
        }
      });
      
      // Wait for chart to render, then get image data
      setTimeout(() => {
        const imageData = canvas.toDataURL('image/png', 1.0);
        chart.destroy();
        resolve(imageData);
      }, 100);
    });
  };

  const selectImageByIndex = (index: number) => {
    setSelectedImageIndex(index);
    setCurrentImageIndex(index);
    setCameraGalleryOpen(false);
  };
  
  const generateExecutiveReport = () => {
    reportReadyRef.current = false;
    setExecutiveReportModel(null);
    setReportExportError(null);
    setExecutiveReportOpen(true);
  };
  
  const downloadExecutiveReport = async () => {
    setReportGenerating(true);
    
    try {
      // Wait for the component to signal it's ready
      await new Promise<void>((resolve) => {
        const checkReady = () => {
          if (reportReadyRef.current) {
            resolve();
          } else {
            setTimeout(checkReady, 100);
          }
        };
        checkReady();
        
        // Fallback timeout after 30 seconds to allow for large data processing
        setTimeout(() => {
          console.warn('Report generation timeout - proceeding anyway');
          resolve();
        }, 30000);
      });
      
      if (!reportRef.current) {
        throw new Error('Report component not found');
      }

      // Capture the entire component at once
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: reportRef.current.scrollWidth,
        height: reportRef.current.scrollHeight
      });

      // Create PDF and let it handle page breaks automatically
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * pageWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages as needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Download the PDF
      const fileName = `NILUS_Executive_Impact_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      setExecutiveReportOpen(false);
      setReportGenerating(false);
    } catch (error) {
      console.error('Error generating report:', error);
      setReportGenerating(false);
    }
  };

  const downloadExecutiveSpreadsheet = async () => {
    if (!executiveReportModel) {
      setReportExportError('Report data is still loading. Please try again.');
      return;
    }
    setSpreadsheetGenerating(true);
    setReportExportError(null);
    try {
      const { downloadExecutiveReportWorkbook } = await import(
        '../utils/executiveReportWorkbook'
      );
      await downloadExecutiveReportWorkbook(executiveReportModel, {
        clientName: sponsorConfig.name,
        companyCode: sponsorSlug,
        seasonYear: sponsorConfig.seasonYear,
        seasonStart: sponsorConfig.seasonStart,
        dataThrough: sponsorConfig.weeklyReportDataThrough,
        goal: sponsorConfig.goal,
        glaciers: sponsorConfig.stupaIds.map(({ id, name }) => ({ id, name })),
      });
    } catch (exportError) {
      console.error('Error generating spreadsheet:', exportError);
      setReportExportError('Unable to generate the XLSX spreadsheet.');
    } finally {
      setSpreadsheetGenerating(false);
    }
  };

  // Helper function to calculate week number since deployment
  const getWeeksSinceDeployment = (date: Date): number => {
    const timeDiff = date.getTime() - stupaDeploymentDate.getTime();
    return Math.floor(timeDiff / (100 * 60 *60 * 24 * 7)) + 1;
  };

  // Helper function to get week range for display - using relative labels
  const getWeekRangeForDisplay = (selectedDate: Date): { startWeek: number, endWeek: number, weekLabels: string[] } => {
    // Use relative week labels (Week 1, Week 2, etc.) instead of absolute week numbers
    // This ensures that data is labeled correctly regardless of when it was collected
    const weekLabels = ['Week 1,Week 2,Week 3,Week 4,Week 5', 'Week 6'];
    
    return {
      startWeek: 1,
      endWeek: 6,
      weekLabels
    };
  };

  // New function to calculate week labels based on actual data availability
  const getActualWeekLabels = (weeklyData: number[]): string[] => {
    if (!weeklyData || weeklyData.length === 0) return ['Week 1,Week 2,Week 3,Week 4,Week 5Week 6'];
    
    // Find the most recent week with data
    let mostRecentWeekWithData = -1;
    for (let i = weeklyData.length - 1; i >= 0; i--) {
      if (weeklyData[i] > 0) {
        mostRecentWeekWithData = i;
        break;
      }
    }
    
    // If no data found, return default labels
    if (mostRecentWeekWithData === -1) return ['Week 1,Week 2,Week 3,Week 4,Week 5Week 6'];
    
    // Calculate how many weeks ago the most recent data is
    const weeksAgo = weeklyData.length - 1 - mostRecentWeekWithData;
    
    // Calculate the actual week numbers
    const today = new Date();
    const currentWeekNumber = getWeeksSinceDeployment(today);
    const startWeek = Math.max(1, currentWeekNumber - 5 - weeksAgo);
    const endWeek = currentWeekNumber - weeksAgo;
    
    const weekLabels = [];
    for (let week = startWeek; week <= endWeek; week++) {
      weekLabels.push(`Week ${week}`);
    }
    
    return weekLabels;
  };

  // Backend dashboard-fast is authoritative for glacier and replenishment status.
  const getOperationalStatus = () => {
    const dashboards = selectedStupa.id === 'home'
      ? Object.values(stupaDataMap)
      : [stupaData];
    const glacierStatuses = dashboards
      .map((dashboard: any) => dashboard?.statuses?.glacier?.status)
      .filter((status) => ['FREEZING', 'MELTING'].includes(status));
    const replenishmentStatuses = dashboards
      .map((dashboard: any) => dashboard?.statuses?.replenishment?.status)
      .filter((status) => ['OPERATIONAL', 'OFFLINE'].includes(status));
    const glacierStatus = glacierStatuses.includes('FREEZING')
      ? 'FREEZING'
      : glacierStatuses.includes('MELTING') ? 'MELTING' : 'NO DATA';
    const replenishmentStatus = replenishmentStatuses.includes('OPERATIONAL')
      ? 'OPERATIONAL'
      : 'OFFLINE';

    const backendCameraStatus = selectedCameraStatus?.status;
    const latestSelectedTimestamp = cameraImages
      .filter((image) => !selectedCameraId || image?.cam_id === selectedCameraId)
      .map((image) => new Date(image?.timestamp).getTime())
      .filter(Number.isFinite)
      .reduce((latest, timestamp) => Math.max(latest, timestamp), Number.NEGATIVE_INFINITY);
    const isStrictlyFresh = Number.isFinite(latestSelectedTimestamp)
      && Date.now() - latestSelectedTimestamp < 7 * 24 * 60 * 60 * 1000;
    const cameraStatus = ['LIVE', 'NO DATA'].includes(backendCameraStatus)
      ? backendCameraStatus
      : isStrictlyFresh ? 'LIVE' : 'NO DATA';
    const glacierAsOf = dashboards
      .map((dashboard: any) => dashboard?.statuses?.glacier?.as_of)
      .filter(Boolean)[0];
    const replenishmentLatest = dashboards
      .map((dashboard: any) => dashboard?.statuses?.replenishment?.latest_timestamp)
      .filter(Boolean)[0];
    
    return {
      glacier: {
        status: glacierStatus,
        color: glacierStatus === 'FREEZING' ? '#10b981' : glacierStatus === 'MELTING' ? '#f59e0b' : '#ef4444',
        description: glacierStatus === 'NO DATA'
          ? 'No valid seven-day ice-volume change is available'
          : `${glacierStatus === 'FREEZING' ? 'Positive' : 'Zero or negative'} seven-day ice-volume change${glacierAsOf ? ` as of ${new Date(glacierAsOf).toLocaleDateString()}` : ''}`
      },
      camera: {
        status: cameraStatus,
        color: cameraStatus === 'LIVE' ? '#10b981' : '#ef4444',
        description: cameraStatus === 'LIVE'
          ? 'The selected camera has an image less than seven days old'
          : 'The selected camera has no image less than seven days old'
      },
      data: {
        status: replenishmentStatus,
        color: replenishmentStatus === 'OPERATIONAL' ? '#10b981' : '#ef4444',
        description: replenishmentStatus === 'OPERATIONAL'
          ? `Replenishment data is less than seven days old${replenishmentLatest ? ` (${new Date(replenishmentLatest).toLocaleDateString()})` : ''}`
          : 'No replenishment record less than seven days old is available'
      }
    };
  };

  // Executive Report Component
  const ExecutiveReportComponent = () => {
    const currentMonthReplenishment = waterReplenishment?.current_month_replenished || 0;
    const totalReplenishment = waterReplenishment?.total_replenished || 0;
    const monthlyData = waterReplenishment?.monthly_replenished || [0, 0, 0, 0, 0, 0];
    const previousMonth = monthlyData.length > 1 ? monthlyData[monthlyData.length - 2] : 0;
    
    // Calculate percentage change
    let changeText = 'Initial period';
    if (previousMonth > 0 && currentMonthReplenishment > 0) {
      const percentChange = ((currentMonthReplenishment - previousMonth) / previousMonth * 100);
      changeText = percentChange >= 0 ? `+${percentChange.toFixed(1)}% vs last period` : `${percentChange.toFixed(1)}% vs last period`;
    } else if (currentMonthReplenishment > 0) {
      changeText = 'Active preservation period';
    }

    return (
      <div ref={reportRef} style={{ 
        width: '794px',
        backgroundColor: '#ffffff',
        fontFamily: 'Arial, sans-serif',
        fontSize: '14px',
        lineHeight: '1.6',
        color: '#333333'
      }}>
        {/* Cover Page Section */}
        <div style={{
          minHeight: '1123px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: 'white',
          padding: '60px',
          position: 'relative'
        }}>
          {/* Logo */}
          <div style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '48px',
            fontWeight: 'bold',
            marginBottom: '40px',
            boxShadow: '0 20px 40px rgba(14, 165, 233, 0.4)'
          }}>
            N
          </div>
          
          <h1 style={{
            fontSize: '48px',
            fontWeight: 'bold',
            marginBottom: '20px',
            letterSpacing: '-0.5px'
          }}>
            GLACIER PRESERVATION<br />IMPACT REPORT
          </h1>
          
          <p style={{
            fontSize: '18px',
            color: '#94a3b8',
            marginBottom: '60px'
          }}>
            Executive Summary for Strategic Partners
          </p>
          
          <div style={{
            background: 'rgba(30, 41, 59, 0.8)',
            border: '2px solid #0ea5e9',
            borderRadius: '12px',
            padding: '40px',
            maxWidth: '600px'
          }}>
            <h3 style={{ marginBottom: '20px', fontSize: '20px' }}>
              {selectedStupa.name} • Cajon de Maipo, Chile
            </h3>
            <p style={{ marginBottom: '10px' }}>
              {stupaData?.stupa_info?.lat?.toFixed(4)}°, {stupaData?.stupa_info?.lon?.toFixed(4)}°
            </p>
            <p>
              Report Generated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          
          <p style={{
            position: 'absolute',
            bottom: '40px',
            fontSize: '14px',
            color: '#64748b'
          }}>
            Powered by NILUS Glacier Monitoring Technology
          </p>
        </div>

        {/* Executive Overview Section */}
        <div style={{
          backgroundColor: '#ffffff',
          padding: '60px'
        }}>
          <div style={{
            borderLeft: '6px solid #0ea5e9',
            paddingLeft: '30px',
            marginBottom: '40px',
            background: '#f8fafc',
            padding: '30px',
            borderRadius: '8px'
          }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#0f172a',
              marginBottom: '10px'
            }}>
              EXECUTIVE OVERVIEW
            </h2>
            <p style={{ fontSize: '16px', color: '#64748b' }}>
              Strategic Impact & Performance Summary
            </p>
          </div>

          <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' }}>
            Dear Valued Partner,
          </h3>
          
          <p style={{ marginBottom: '20px', fontSize: '16px', lineHeight: '1.8' }}>
            We are pleased to present this comprehensive impact report on your glacier preservation investment. 
            Through cutting-edge monitoring technology and sustainable engineering, we continue to deliver 
            measurable environmental impact while providing transparent accountability to our strategic partners.
          </p>
          
          <p style={{ marginBottom: '40px', fontSize: '16px', lineHeight: '1.8' }}>
            This quarter demonstrates the tangible results of our collaborative commitment to climate action 
            and water security innovation.
          </p>

          {/* Key Metrics Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '20px',
            marginBottom: '60px'
          }}>
            <div style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderLeft: '6px solid #10b981',
              borderRadius: '8px',
              padding: '24px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <h4 style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#0f172a',
                marginBottom: '8px'
              }}>
                {currentMonthReplenishment > 0 ? `${currentMonthReplenishment}m³` : 'Monitoring Phase'}
              </h4>
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                Water Preserved This Period
              </p>
              <p style={{ fontSize: '12px', color: '#10b981', fontWeight: 'bold' }}>
                {changeText}
              </p>
            </div>

            <div style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderLeft: '6px solid #10b981',
              borderRadius: '8px',
              padding: '24px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <h4 style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#0f172a',
                marginBottom: '8px'
              }}>
                {totalReplenishment > 0 ? `${(totalReplenishment / 1000).toFixed(1)}K m³` : 'System Deployment'}
              </h4>
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                Total Impact to Date
              </p>
              <p style={{ fontSize: '12px', color: '#10b981', fontWeight: 'bold' }}>
                {totalReplenishment > 0 ? 'Cumulative achievement' : 'Infrastructure established'}
              </p>
            </div>

            <div style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderLeft: '6px solid #10b981',
              borderRadius: '8px',
              padding: '24px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <h4 style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#0f172a',
                marginBottom: '8px'
              }}>
                {stupaData?.freezing_stats?.total_hours_below_freezing !== undefined && stupaData?.freezing_stats?.total_possible_hours !== undefined && stupaData?.freezing_stats?.total_possible_hours > 0 ? 
                  `${((stupaData.freezing_stats.total_hours_below_freezing / stupaData.freezing_stats.total_possible_hours) * 100).toFixed(0)}%` : '0%'}
              </h4>
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                System Efficiency
              </p>
              <p style={{ fontSize: '12px', color: '#10b981', fontWeight: 'bold' }}>
                Optimal environmental conditions
              </p>
            </div>

            <div style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderLeft: `6px solid ${getOperationalStatus().glacier.color}`,
              borderRadius: '8px',
              padding: '24px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <h4 style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#0f172a',
                marginBottom: '8px'
              }}>
                {getOperationalStatus().glacier.status}
              </h4>
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                Glacier System Status
              </p>
              <p style={{ fontSize: '12px', color: getOperationalStatus().glacier.color, fontWeight: 'bold' }}>
                {getOperationalStatus().glacier.description}
              </p>
            </div>
          </div>
        </div>

        {/* Investment Highlights & Performance Section */}
        <div style={{
          backgroundColor: '#ffffff',
          padding: '60px'
        }}>
          <div style={{
            borderLeft: '6px solid #0ea5e9',
            paddingLeft: '30px',
            marginBottom: '40px',
            background: '#f8fafc',
            padding: '30px',
            borderRadius: '8px'
          }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#0f172a',
              marginBottom: '10px'
            }}>
              INVESTMENT HIGHLIGHTS
            </h2>
            <p style={{ fontSize: '16px', color: '#64748b' }}>
              Demonstrating Value & Impact
            </p>
          </div>

          <div style={{ fontSize: '16px', lineHeight: '2.2', marginBottom: '60px' }}>
            <p style={{ marginBottom: '16px' }}>• Advanced IoT monitoring delivering 99.8% data reliability</p>
            <p style={{ marginBottom: '16px' }}>• Sustainable water preservation technology with measurable impact</p>
            <p style={{ marginBottom: '16px' }}>• Real-time environmental monitoring and reporting capabilities</p>
            <p style={{ marginBottom: '16px' }}>• Strategic partnership with leading climate technology provider</p>
            <p style={{ marginBottom: '16px' }}>• Transparent reporting and accountability framework</p>
            <p style={{ marginBottom: '16px' }}>• Scalable solution architecture for future expansion</p>
          </div>

          {/* Performance Chart */}
          <div style={{ marginBottom: '60px' }}>
            <h3 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#0f172a',
              marginBottom: '30px'
            }}>
              PERFORMANCE ANALYTICS
            </h3>
            
            <div style={{
              background: '#f8fafc',
              padding: '30px',
              borderRadius: '12px',
              border: '1px solid #e2e8f0'
            }}>
              <h4 style={{ fontSize: '18px', marginBottom: '20px', textAlign: 'center' }}>
                Water Preservation Performance Trend
              </h4>
              
              {/* Simple bar chart */}
              <div style={{
                display: 'flex',
                alignItems: 'end',
                justifyContent: 'space-around',
                height: '200px',
                background: 'white',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                {monthlyData.map((value: number, index: number) => {
                  const maxValue = Math.max(...monthlyData, 1);
                  const height = (value / maxValue) * 140;
                  return (
                    <div key={index} style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <div style={{
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: '#0f172a'
                      }}>
                        {value}m³
                      </div>
                      <div style={{
                        width: '40px',
                        height: `${Math.max(height, 20)}px`,
                        background: index === monthlyData.length - 1 ? 
                          'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' : 
                          'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                        borderRadius: '4px 4px 0 0',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }} />
                      <div style={{
                        fontSize: '11px',
                        color: '#64748b',
                        textAlign: 'center'
                      }}>
                        Week {index + 1}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Strategic Partnership Section */}
        <div style={{
          backgroundColor: '#ffffff',
          padding: '60px'
        }}>
          <div style={{
            borderLeft: '6px solid #0ea5e9',
            paddingLeft: '30px',
            marginBottom: '40px',
            background: '#f8fafc',
            padding: '30px',
            borderRadius: '8px'
          }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#0f172a',
              marginBottom: '10px'
            }}>
              STRATEGIC PARTNERSHIP
            </h2>
            <p style={{ fontSize: '16px', color: '#64748b' }}>
              Continued Collaboration & Growth
            </p>
          </div>

          <h3 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#0f172a',
            marginBottom: '20px'
          }}>
            NILUS COMMITMENT TO EXCELLENCE
          </h3>
          
          <p style={{ fontSize: '16px', marginBottom: '30px', lineHeight: '1.8' }}>
            As your trusted technology partner, NILUS continues to deliver:
          </p>

          <div style={{ fontSize: '16px', lineHeight: '2.0', marginBottom: '50px' }}>
            <p style={{ marginBottom: '14px' }}>• Continuous Innovation: Advanced monitoring and preservation technologies</p>
            <p style={{ marginBottom: '14px' }}>• Transparent Reporting: Regular performance updates and impact assessments</p>
            <p style={{ marginBottom: '14px' }}>• Expert Support: 24/7 technical monitoring and maintenance services</p>
            <p style={{ marginBottom: '14px' }}>• Scalable Solutions: Future-ready infrastructure for expansion opportunities</p>
            <p style={{ marginBottom: '14px' }}>• Environmental Impact: Measurable contribution to climate preservation goals</p>
          </div>

          <h3 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#0f172a',
            marginBottom: '20px'
          }}>
            RECOMMENDED NEXT STEPS
          </h3>

          <div style={{ fontSize: '16px', lineHeight: '2.0', marginBottom: '50px' }}>
            {cameraOfflineWarning && (
              <p style={{ marginBottom: '14px' }}>• Schedule routine maintenance visit to optimize monitoring systems</p>
            )}
            {(currentMonthReplenishment || 0) < 1000 && (
              <p style={{ marginBottom: '14px' }}>• Environmental assessment to maximize preservation efficiency</p>
            )}
            <p style={{ marginBottom: '14px' }}>• Quarterly strategic review to assess expansion opportunities</p>
            <p style={{ marginBottom: '14px' }}>• Continued monitoring and performance optimization</p>
            <p style={{ marginBottom: '14px' }}>• Stakeholder briefing on long-term preservation strategy</p>
          </div>

          {/* Contact Section */}
          <div style={{
            background: '#f8fafc',
            padding: '30px',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            marginBottom: '40px'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#0f172a',
              marginBottom: '15px'
            }}>
              PARTNERSHIP SUPPORT
            </h3>
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '10px' }}>
              For strategic inquiries and partnership development:
            </p>
            <p style={{ fontSize: '16px', fontWeight: 'bold', color: '#0ea5e9', marginBottom: '10px' }}>
              partnerships@nilus.com • +1 (555) 123-4567
            </p>
            <p style={{ fontSize: '14px', color: '#64748b' }}>
              Technical support available 24/7 for all monitoring systems
            </p>
          </div>

          {/* Footer */}
          <div style={{
            borderTop: '3px solid #0ea5e9',
            paddingTop: '20px',
            marginTop: '40px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '14px',
            color: '#64748b'
          }}>
            <span>NILUS Glacier Preservation Technology</span>
            <span>Report ID: {Date.now().toString(36).toUpperCase()}</span>
          </div>
          <div style={{
            textAlign: 'center',
            marginTop: '10px',
            fontSize: '12px',
            color: '#94a3b8'
          }}>
            Confidential - For Strategic Partners Only
          </div>
        </div>
      </div>
    );
  };

  // Add a reusable loading spinner component
  const LoadingSpinner = ({ size = '20px' }: { size?: string }) => (
    <div style={{
      width: size,
      height: size,
      border: '2px solid rgba(148, 163, 184, 0.3)',
      borderTop: '2px solid #0ea5e9',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      margin: '0 auto'
    }} />
  );

  // Loading card component for metrics
  const LoadingCard = ({ children }: { children: React.ReactNode }) => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      opacity: 0.8,
      minHeight: '60px' // Ensure consistent height with actual values
    }}>
      <LoadingSpinner size="16px" />
      <div style={{ 
        fontSize: '0.65rem', 
        color: '#64748b',
        textAlign: 'center',
        lineHeight: '1.2'
      }}>
        Loading...
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px',
        position: 'relative'
      }}>
        {/* Animated background pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          animation: 'backgroundPulse 4s ease-in-out infinite'
        }} />
        
        {/* Main loading container */}
        <div style={{
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
          borderRadius: '20px',
          padding: '60px 80px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(148, 163, 184, 0.1)',
          border: '1px solid rgba(148, 163, 184, 0.2)',
          textAlign: 'center',
          maxWidth: '600px',
          width: '100%',
          position: 'relative',
          zIndex: 2
        }}>
          {/* Logo/Header */}
          <div style={{ marginBottom: '40px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 30px rgba(14, 165, 233, 0.4)',
              animation: 'logoSpin 3s ease-in-out infinite'
            }}>
              <AcUnit style={{ fontSize: '40px', color: 'white' }} />
            </div>
            <Typography variant="h4" style={{
              color: '#f8fafc',
              fontWeight: 700,
              marginBottom: '12px',
              letterSpacing: '-0.025em'
            }}>
              Glacier Monitoring System
            </Typography>
            <Typography style={{
              color: '#94a3b8',
              fontSize: '1.1rem',
              fontWeight: 500
            }}>
              Initializing secure connection to your glacier(s)
            </Typography>
          </div>

          {/* Progress indicator */}
          <div style={{ marginBottom: '40px' }}>
            <div style={{
              background: 'rgba(15, 23, 42, 0.5)',
              borderRadius: '12px',
              padding: '4px',
              marginBottom: '20px'
            }}>
              <div style={{
                height: '8px',
                background: 'linear-gradient(90deg, #0ea5e9 0%, #0284c7 100%)',
                borderRadius: '8px',
                width: loadingSteps.complete ? '100%' : 
                       loadingSteps.images ? '75%' : 
                       loadingSteps.stupaData ? '50%' : 
                       loadingSteps.connecting ? '25%' : '10%',
                transition: 'width 0.8s ease-out',
                boxShadow: '0 0 15px rgba(14, 165, 233, 0.5)'
              }} />
            </div>
            
            {/* Status message */}
            <Typography style={{
              color: '#e2e8f0',
              fontSize: '1.1rem',
              fontWeight: 600,
              marginBottom: '8px'
            }}>
              {loadingMessage}
            </Typography>
            
            {/* Sub-status */}
            <Typography style={{
              color: '#64748b',
              fontSize: '0.9rem'
            }}>
              {loadingSteps.connecting && 'Establishing encrypted connection...'}
              {loadingSteps.stupaData && 'Retrieving sensor data and analytics...'}
              {loadingSteps.images && 'Loading camera feed and recent images...'}
              {loadingSteps.complete && 'Ready! Redirecting to dashboard...'}
            </Typography>
          </div>

          {/* Loading steps visualization */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '30px'
          }}>
            {[
              { key: 'connecting', icon: '🔐', label: 'Connect' },
              { key: 'stupaData', icon: '📊', label: 'Data' },
              { key: 'images', icon: '📷', label: 'Images' },
              { key: 'complete', icon: '✅', label: 'Ready' }
            ].map((step, index) => {
              const isActive = loadingSteps[step.key as keyof typeof loadingSteps];
              const isCompleted = index < (
                loadingSteps.complete ? 4 : 
                loadingSteps.images ? 3 : 
                loadingSteps.stupaData ? 2 : 
                loadingSteps.connecting ? 1 : 0
              );
              
              return (
                <div key={step.key} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  opacity: isCompleted || isActive ? 1 : 0.4,
                  transition: 'all 0.5s ease'
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    background: isCompleted 
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : isActive 
                        ? 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)'
                        : 'rgba(71, 85, 105, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    marginBottom: '8px',
                    border: isActive ? '2px solid rgba(14, 165, 233, 0.5)' : 'none',
                    boxShadow: isActive ? '0 0 20px rgba(14, 165, 233, 0.4)' : 'none',
                    animation: isActive ? 'stepPulse 2s ease-in-out infinite' : 'none'
                  }}>
                    {step.icon}
                  </div>
                  <Typography style={{
                    fontSize: '0.8rem',
                    color: isCompleted || isActive ? '#e2e8f0' : '#64748b',
                    fontWeight: 600
                  }}>
                    {step.label}
                  </Typography>
                </div>
              );
            })}
          </div>

          {/* Loading spinner */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              border: '3px solid rgba(14, 165, 233, 0.3)',
              borderTop: '3px solid #0ea5e9',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <Typography style={{
              color: '#94a3b8',
              fontSize: '0.9rem',
              fontWeight: 500
            }}>
              Please wait while we prepare your dashboard...
            </Typography>
          </div>
        </div>

        {/* CSS Animations */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes logoSpin {
              0%, 100% { transform: rotate(0deg) scale(1); }
              50% { transform: rotate(10deg) scale(1.05); }
            }
            @keyframes stepPulse {
              0%, 100% { transform: scale(1); box-shadow: 0 0 20px rgba(14, 165, 233, 0.4); }
              50% { transform: scale(1.1); box-shadow: 0 0 30px rgba(14, 165, 233, 0.6); }
            }
            @keyframes backgroundPulse {
              0%, 100% { opacity: 0.5; }
              50% { opacity: 0.8; }
            }
          `}
        </style>
      </div>
    );
  }

  // Error state display
  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px',
        position: 'relative'
      }}>
        {/* Animated background pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          animation: 'backgroundPulse 4s ease-in-out infinite'
        }} />
        
        {/* Error container */}
        <div style={{
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
          borderRadius: '20px',
          padding: '60px 80px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(239, 68, 68, 0.2)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          textAlign: 'center',
          maxWidth: '600px',
          width: '100%',
          position: 'relative',
          zIndex: 2
        }}>
          {/* Error Icon */}
          <div style={{ marginBottom: '40px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 30px rgba(239, 68, 68, 0.4)',
              animation: 'errorPulse 2s ease-in-out infinite'
            }}>
              <span style={{ fontSize: '40px', color: 'white' }}>⚠️</span>
            </div>
            <Typography variant="h4" style={{
              color: '#f8fafc',
              fontWeight: 700,
              marginBottom: '12px',
              letterSpacing: '-0.025em'
            }}>
              Connection Failed
            </Typography>
            <Typography style={{
              color: '#94a3b8',
              fontSize: '1.1rem',
              fontWeight: 500
            }}>
              Unable to connect to monitoring station
            </Typography>
          </div>

          {/* Error details */}
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '40px'
          }}>
            <Typography style={{
              color: '#fca5a5',
              fontSize: '0.9rem',
              fontWeight: 600,
              marginBottom: '8px'
            }}>
              Error Details:
            </Typography>
            <Typography style={{
              color: '#e2e8f0',
              fontSize: '0.9rem',
              lineHeight: 1.5
            }}>
              {error}
            </Typography>
          </div>

          {/* Troubleshooting steps */}
          <div style={{
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '30px',
            textAlign: 'left'
          }}>
            <Typography style={{
              color: '#93c5fd',
              fontSize: '0.9rem',
              fontWeight: 600,
              marginBottom: '12px'
            }}>
              Troubleshooting Steps:
            </Typography>
            <ul style={{
              color: '#e2e8f0',
              fontSize: '0.85rem',
              lineHeight: 1.6,
              margin: 0,
              paddingLeft: '20px'
            }}>
              <li>Check your internet connection</li>
              <li>Verify the monitoring station is online</li>
              <li>Try refreshing the page</li>
              <li>Contact technical support if the issue persists</li>
            </ul>
          </div>

          {/* Action buttons */}
          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: 'white',
                padding: '12px 32px',
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)',
                border: 'none'
              }}
            >
              Retry Connection
            </Button>
          </div>

          {/* Status indicator */}
          <div style={{
            marginTop: '30px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#ef4444',
              animation: 'pulse 2s ease-in-out infinite'
            }} />
            <Typography style={{
              color: '#94a3b8',
              fontSize: '0.8rem',
              fontWeight: 500
            }}>
              Last connection attempt: {new Date().toLocaleTimeString()}
            </Typography>
          </div>
        </div>

        {/* CSS Animations */}
        <style>
          {`
            @keyframes errorPulse {
              0%, 100% { transform: scale(1); box-shadow: 0 10px 30px rgba(239, 68, 68, 0.4); }
              50% { transform: scale(1.05); box-shadow: 0 15px 40px rgba(239, 68, 68, 0.6); }
            }
            @keyframes backgroundPulse {
              0%, 100% { opacity: 0.5; }
              50% { opacity: 0.8; }
            }
            @keyframes pulse {
              0% { opacity: 0.5; }
              50% { opacity: 1; }
              100% { opacity: 0.5; }
            }
          `}
        </style>
      </div>
    );
  }

  // Helper functions to get current data to display
  const getCurrentDisplayData = () => {
    const isViewingToday = currentViewDate.toDateString() === new Date().toDateString();
    
    // Handle home view (cumulative stats for all stupas)
    if (selectedStupa.id === 'home') {
      const cumulativeStats = getCumulativeStats();
      return {
        stupaData: {
          replenishment_stats: {
            total_managed: cumulativeStats.totalManaged,
            total_replenished: cumulativeStats.totalReplenished,
            average_monthly_replenished: averageMonthlyGrowthPerStupa(
              cumulativeStats.averageMonthlyReplenished,
              sponsorConfig.stupaIds.length,
            ),
            monthly_replenished: Object.values(cumulativeStats.stackedMonthly).map(month => 
              Object.values(month).reduce((sum: number, val: any) => sum + (val || 0), 0)
            )
          },
          freezing_stats: {
            harvested_freezing_hours: cumulativeStats.freezingHours
          }
        },
        waterData: {
          total_replenished: cumulativeStats.totalReplenished,
          average_monthly_replenished: averageMonthlyGrowthPerStupa(
            cumulativeStats.averageMonthlyReplenished,
            sponsorConfig.stupaIds.length,
          ),
          monthly_replenished: Object.values(cumulativeStats.stackedMonthly).map(month => 
            Object.values(month).reduce((sum: number, val: any) => sum + (val || 0), 0)
          )
        },
        cameraData: cameraImages,
        isHistorical: false,
        isLoading: false
      };
    }
    
    if (isViewingToday) {
      return {
        stupaData: stupaData,
        waterData: {
          monthly_replenished: waterReplenishment?.monthly_replenished || [],
          total_replenished: waterReplenishment?.total_replenished || 0,
          current_month_replenished: waterReplenishment?.current_month_replenished || 0,
          last_30_days_replenished: waterReplenishment?.last_30_days_replenished || 0,
          average_monthly_replenished: waterReplenishment?.average_monthly_replenished || 0,
          average_month_start: waterReplenishment?.average_month_start || null,
          average_month_end: waterReplenishment?.average_month_end || null,
        },
        cameraData: cameraImages,
        isHistorical: false,
        isLoading: false
      };
    } else {
      const historicalWaterDataAdjusted = historicalWaterData ? {
        monthly_replenished: historicalWaterData.monthly_replenished || [],
        total_replenished: historicalWaterData.total_replenished || 0,
        current_month_replenished: historicalWaterData.current_month_replenished || 0,
        last_30_days_replenished: historicalWaterData.last_30_days_replenished || 0,
        average_monthly_replenished: historicalWaterData.average_monthly_replenished || 0,
        average_month_start: historicalWaterData.average_month_start || null,
        average_month_end: historicalWaterData.average_month_end || null,
      } : {
        monthly_replenished: waterReplenishment?.monthly_replenished || [],
        total_replenished: waterReplenishment?.total_replenished || 0,
        current_month_replenished: waterReplenishment?.current_month_replenished || 0,
        last_30_days_replenished: waterReplenishment?.last_30_days_replenished || 0,
        average_monthly_replenished: waterReplenishment?.average_monthly_replenished || 0,
        average_month_start: waterReplenishment?.average_month_start || null,
        average_month_end: waterReplenishment?.average_month_end || null,
      };
      
      return {
        stupaData: historicalStupaData || stupaData,
        waterData: historicalWaterDataAdjusted,
        cameraData: historicalCameraImages.length > 0 ? historicalCameraImages : cameraImages,
        isHistorical: true,
        isLoading: isLoadingHistorical
      };
    }
  };

  const currentImage = selectedStupa.id === 'home' ? null : 
    (getCurrentDisplayData().cameraData?.[currentImageIndex] || stupaData?.latest_image);

  // Function to fetch historical data for a specific date
  const fetchHistoricalData = async (selectedDate: Date) => {
    setIsLoadingHistorical(true);
    
    try {
      const dateString = selectedDate.toISOString().split('T')[0];
      
      // Fetch historical stupa data for the selected date
      const stupaResponse = await authenticatedFetch(
        `${stupaApiUrl(selectedStupa.id, 'historical-data')}?date=${dateString}`
      );
      if (stupaResponse.ok) {
        const stupaData = await stupaResponse.json();
        setHistoricalStupaData(stupaData);
      } else {
        console.error('Failed to fetch historical stupa data:', stupaResponse.status);
        setHistoricalStupaData(null);
      }
      
      // Fetch historical water replenishment data (monthly data around this date)
      const waterResponse = await authenticatedFetch(
        `${stupaApiUrl(selectedStupa.id, 'historical-water')}?date=${dateString}&months=6`
      );
      if (waterResponse.ok) {
        const waterData = await waterResponse.json();
        setHistoricalWaterData(waterData);
      } else {
        console.error('Failed to fetch historical water data:', waterResponse.status);
        setHistoricalWaterData(null);
      }
      
      // Fetch historical camera images for the selected date
      const cameraResponse = await authenticatedFetch(
        companyImagesUrl(dateString, 8, 1)
      );
      if (cameraResponse.ok) {
        const cameraData = await cameraResponse.json();
        setHistoricalCameraImages(cameraData.images || []);
      } else {
        console.error('Failed to fetch historical camera data:', cameraResponse.status);
        setHistoricalCameraImages([]);
      }
      
    } catch (error) {
      console.error('Error fetching historical data:', error);
      // Clear historical data on error
      setHistoricalStupaData(null);
      setHistoricalWaterData(null);
      setHistoricalCameraImages([]);
    } finally {
      setIsLoadingHistorical(false);
      setIsImageLoadingForTime(false);
    }
  };

  // Helper function to check if image is simulated
  const isSimulatedImage = (fileName: string) => {
    return fileName && fileName.includes('simulated_');
  };

  // Helper function to get image source
  const getImageSource = (image: any) => {
    if (!image) return undefined;
    
    // Check if this is a demo/fallback image
    if (image.file_path && image.file_path.includes('/demo/')) {
      return undefined; // Don't try to load demo images
    }
    
    // Customer dashboards and admin customer previews use the customer-safe view.
    if (image.cam_id && image.file_name) {
      return `${companyApiBase}/image?cam_id=${encodeURIComponent(image.cam_id)}&file_name=${encodeURIComponent(image.file_name)}`;
    }
    
    return undefined;
  };

  // Helper to get cumulative stats for all stupas
  const getCumulativeStats = () => {
    // Aggregate stats for all sponsorConfig.stupaIds with ownership percentages
    let totalManaged = 0;
    let totalReplenished = 0;
    let averageMonthlyReplenished = 0;
    const harvestedHours: unknown[] = [];
    let totalMonths = 0;
    let monthlyDataByStupa: Record<string, number[]> = {};
    let maxMonths = 0;
    let allMonthlyData: number[][] = [];
    let currentWeekReplenishedByStupa: Record<string, number> = {};

    // Get the current week number (ISO week)
    const now = new Date();
    const getISOWeek = (date: Date) => {
      const tmp = new Date(date.getTime());
      tmp.setHours(0, 0, 0, 0);
      tmp.setDate(tmp.getDate() + 4 - (tmp.getDay() || 7));
      const yearStart = new Date(tmp.getFullYear(), 0, 1);
      const weekNo = Math.ceil((((tmp.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
      return `${tmp.getFullYear()}-W${weekNo}`;
    };
    const thisWeek = getISOWeek(now);

    sponsorConfig.stupaIds.forEach(stupa => {
      const stupaStats = stupaDataMap[stupa.id]?.replenishment_stats || {};
      const freezingStats = stupaDataMap[stupa.id]?.freezing_stats || {};
      const waterStats = waterDataMap[stupa.id] || {};
      
      totalManaged += stupaStats.total_managed || 0;
      totalReplenished += stupaStats.total_replenished || 0;
      averageMonthlyReplenished += Number(
        waterStats.average_monthly_replenished || 0
      );
      
      harvestedHours.push(freezingStats.harvested_freezing_hours);
      
      const monthly = waterStats.monthly_replenished || [];
      const adjustedMonthly = monthly;
      monthlyDataByStupa[stupa.id] = adjustedMonthly;
      allMonthlyData.push(adjustedMonthly);
      if (adjustedMonthly.length > maxMonths) maxMonths = adjustedMonthly.length;
      totalMonths += adjustedMonthly.filter((m: number) => m > 0).length;

      // Calculate this week's replenished value from stupaStats.weekly_replenished_by_week if available, else fallback to 0
      let weekValue = 0;
      if (stupaStats.weekly_replenished_by_week && typeof stupaStats.weekly_replenished_by_week === 'object') {
        weekValue = stupaStats.weekly_replenished_by_week[thisWeek] || 0;
      } else if (typeof stupaStats.weekly_replenished === 'number') {
        weekValue = stupaStats.weekly_replenished;
      }
      currentWeekReplenishedByStupa[stupa.id] = weekValue;
    });

    // Build stacked monthly data: for each month, array of {stupaId, value}
    let stackedMonthly: { [month: number]: { [stupaId: string]: number } } = {};
    for (let i = 0; i < maxMonths; i++) {
      stackedMonthly[i] = {};
      sponsorConfig.stupaIds.forEach(stupa => {
        stackedMonthly[i][stupa.id] = monthlyDataByStupa[stupa.id]?.[i] || 0;
      });
    }

    // Calculate average volume per month (across all stupas)
    let avgVolume = 0;
    if (totalMonths > 0) {
      avgVolume = totalReplenished / totalMonths;
    }

    return {
      totalManaged,
      totalReplenished,
      averageMonthlyReplenished,
      freezingHours: maxAvailableHarvestedHours(harvestedHours),
      avgVolume,
      stackedMonthly,
      maxMonths,
      monthlyDataByStupa,
      currentWeekReplenishedByStupa
    };
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
      paddingTop: '80px',
      position: 'relative'
    }}>
      {/* Subtle geometric pattern overlay */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.02'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        pointerEvents: 'none',
        zIndex: 1
      }} />

      <Container maxWidth="xl" style={{ padding: '32px 20px', position: 'relative', zIndex: 2 }}> {/* Reduced from 40px 24px */}
        {preview && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            marginBottom: 24,
            padding: '12px 16px',
            borderRadius: 10,
            color: '#e0f2fe',
            background: 'rgba(14, 165, 233, 0.18)',
            border: '1px solid rgba(56, 189, 248, 0.45)',
          }}>
            <Typography>
              Previewing the customer experience for {sponsorConfig.name}
            </Typography>
            <Button
              component={RouterLink}
              to="/dashboard/operations"
              variant="outlined"
              size="small"
              sx={{ color: '#e0f2fe', borderColor: '#7dd3fc' }}
            >
              Back to dashboard
            </Button>
          </div>
        )}

        {/* Header Section */}
        <div style={{
          marginBottom: '32px',
        }}>
          <Typography variant="h3" style={{
            fontSize: '2.5rem',
            fontWeight: 700,
            color: '#f8fafc',
            marginBottom: '16px',
            letterSpacing: '-0.025em'
          }}>
            Glacier Monitoring Command Center
          </Typography>
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '24px',
            flexWrap: 'wrap',
          }}>
            {/* Stupa Selector - Only show when not on home page */}
            {selectedStupa.id !== 'home' && (
              <div style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                borderRadius: '8px',
                padding: '12px 16px',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                maxWidth: '500px'
              }}>
                <Typography style={{
                  fontSize: '0.8rem',
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  fontWeight: 600,
                  whiteSpace: 'nowrap'
                }}>
                  Glacier:
                </Typography>
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center'
                }}>
                  {/* Home button for multi-stupa sponsors */}
                  {sponsorConfig.stupaIds.length > 1 && (
                    <div
                      key="home"
                      onClick={() => setSelectedStupa({ id: 'home', name: 'All Stupas' })}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        background: selectedStupa.id === 'home'
                          ? 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)'
                          : 'linear-gradient(135deg, #475569 0%, #64748b 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        border: selectedStupa.id === 'home'
                          ? '2px solid rgba(14, 165, 233, 0.5)'
                          : '2px solid transparent',
                        boxShadow: selectedStupa.id === 'home'
                          ? '0 4px 15px rgba(14, 165, 233, 0.4)'
                          : '0 2px 8px rgba(0, 0, 0, 0.2)',
                        position: 'relative'
                      }}
                    >
                      <HomeIcon style={{ fontSize: '22px', color: 'white' }} />
                    </div>
                  )}
                  {/* Existing stupa buttons */}
                  {sponsorConfig.stupaIds.map((stupa) => (
                    <div
                      key={stupa.id}
                      onClick={() => handleStupaSwitch(stupa)}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        background: selectedStupa.id === stupa.id 
                          ? 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)'
                          : 'linear-gradient(135deg, #475569 0%, #64748b 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        border: selectedStupa.id === stupa.id 
                          ? '2px solid rgba(14, 165, 233, 0.5)'
                          : '2px solid transparent',
                        boxShadow: selectedStupa.id === stupa.id 
                          ? '0 4px 15px rgba(14, 165, 233, 0.4)'
                          : '0 2px 8px rgba(0, 0, 0, 0.2)',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedStupa.id !== stupa.id) {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #64748b 0%, #475569 100%)';
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedStupa.id !== stupa.id) {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #475569 0%, #64748b 100%)';
                          e.currentTarget.style.transform = 'scale(1)';
                        }
                      }}
                    >
                      <AcUnit style={{ 
                        fontSize: '20px', 
                        color: 'white',
                        filter: selectedStupa.id === stupa.id ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' : 'none'
                      }} />
                      {/* Stupa number indicator */}
                      <div style={{
                        position: 'absolute',
                        bottom: '-2px',
                        right: '-2px',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        background: selectedStupa.id === stupa.id ? '#10b981' : '#64748b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        color: 'white',
                        border: '1px solid #1e293b'
                      }}>
                        {stupa.id.slice(-1)} {/* Extract number from id */}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Current selection indicator */}
                <div style={{
                  padding: '6px 12px',
                  background: 'rgba(14, 165, 233, 0.15)',
                  borderRadius: '6px',
                  border: '1px solid rgba(14, 165, 233, 0.3)',
                  flex: 1
                }}>
                  <Typography style={{
                    fontSize: '0.9rem',
                    color: '#0ea5e9',
                    fontWeight: 600,
                    whiteSpace: 'nowrap'
                  }}>
                    {selectedStupa.name}
                  </Typography>
                  <Typography style={{
                    fontSize: '0.75rem',
                    color: '#94a3b8',
                    marginTop: '2px'
                  }}>
                    Real-time insights • Camera: {selectedCameraId || 'No camera assigned'}
                  </Typography>
                  {sponsorConfig.cameras.length > 1 && (
                    <select
                      aria-label="Camera"
                      value={selectedCameraId}
                      onChange={(event) => setSelectedCameraId(event.target.value)}
                      style={{ marginTop: 6, minWidth: 120 }}
                    >
                      {sponsorConfig.cameras.map((cameraId) => (
                        <option key={cameraId} value={cameraId}>{cameraId}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            )}
            {/* Sponsor Logo Tile */}
            <div style={{
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              padding: '24px 32px',
              borderRadius: '12px',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(148, 163, 184, 0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              minWidth: '320px'
            }}>
              {sponsorConfig.logo && !logoError ? (
                logoIsProtected ? (
                  <AuthenticatedImage
                    src={logoUrl}
                    alt={sponsorConfig.name}
                    style={{ width: '120px', height: '56px', objectFit: 'contain', flexShrink: 0 }}
                    onLoadError={() => setLogoError(true)}
                  />
                ) : (
                  <img
                    src={logoUrl}
                    alt={sponsorConfig.name}
                    style={{ width: '120px', height: '56px', objectFit: 'contain', flexShrink: 0 }}
                    onError={() => setLogoError(true)}
                  />
                )
              ) : (
                <div style={{
                  width: 120,
                  height: 56,
                  borderRadius: 8,
                  display: 'grid',
                  placeItems: 'center',
                  background: '#475569',
                  color: '#f8fafc',
                  fontWeight: 700,
                  fontSize: 18,
                }}>
                  {sponsorConfig.name}
                </div>
              )}
              <div>
                <Typography style={{
                  fontSize: '0.75rem',
                  color: '#64748b',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  fontWeight: 600
                }}>
                  {sponsorConfig.description}
                </Typography>
                <Typography style={{
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  color: '#f8fafc',
                  lineHeight: 1.2
                }}>
                  {sponsorConfig.name}
                </Typography>
              </div>
            </div>
          </div>
        </div>

        <Grid container spacing={4}>
          
          {/* Left Column - Live Feed with Enhanced Controls */}
          <Grid item xs={12} lg={5}>
            <Card style={{
              borderRadius: '16px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(148, 163, 184, 0.1)',
              border: '1px solid rgba(148, 163, 184, 0.2)',
              overflow: 'hidden',
              height: '750px', // Increased height to give more room
              position: 'relative',
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
            }}>
              {/* Camera Feed */}
              <div style={{
                position: 'relative',
                height: '85%', // Increased from 75% to give more room to the image now that slider is removed
                backgroundColor: '#1e293b',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {/* Home View - Show the selected company-authorized camera image */}
                {selectedStupa.id === 'home' && (
                  <>
                    {(() => {
                      const firstImage = cameraImages[0];
                      
                      if (firstImage && !imageError && getImageSource(firstImage)) {
                        return (
                          <AuthenticatedImage
                            src={getImageSource(firstImage)}
                            alt={`${selectedCameraId} camera feed`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '14px',
                              opacity: imageLoading ? 0.5 : 1,
                              transition: 'opacity 0.3s ease',
                              cursor: 'pointer'
                            }}
                            onClick={openCameraGallery}
                            onLoadError={() => {
                              setImageError(true);
                              setImageLoading(false);
                              setHasInitialImageLoaded(true);
                            }}
                            onLoad={() => {
                              setImageError(false);
                              setImageLoading(false);
                              setHasInitialImageLoaded(true);
                            }}
                          />
                        );
                      } else {
                        return (
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#94a3b8',
                            textAlign: 'center',
                            padding: '40px'
                          }}>
                            <PhotoCamera style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                            <Typography style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '8px' }}>
                              {imageError ? 'Camera Image Failed to Load' : 'Camera Feed Unavailable'}
                            </Typography>
                            <Typography style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                              {imageError
                                ? 'The latest selected-camera image could not be displayed.'
                                : 'No camera data is available for the selected camera.'}
                            </Typography>
                          </div>
                        );
                      }
                    })()}
                  </>
                )}

                {/* Regular image display */}
                {selectedStupa.id !== 'home' && currentImage && !imageError && getImageSource(currentImage) && (
                  <>
                    <AuthenticatedImage
                      src={getImageSource(currentImage)}
                      alt="Glacier camera feed"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '14px',
                        opacity: imageLoading ? 0.5 : 1,
                        transition: 'opacity 0.3s ease',
                        cursor: 'pointer'
                      }}
                      onClick={openCameraGallery}
                      onLoadError={() => {
                        setImageError(true);
                        setImageLoading(false);
                        setHasInitialImageLoaded(true); // Mark as loaded even if error
                      }}
                      onLoad={() => {
                        setImageError(false);
                        setImageLoading(false);
                        setHasInitialImageLoaded(true); // Mark as loaded when image loads
                      }}
                    />
                    
                    {/* Initial loading indicator - shows until first image loads */}
                    {!hasInitialImageLoaded && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(30, 41, 59, 0.95)',
                        borderRadius: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 15
                      }}>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '16px',
                          color: '#e2e8f0'
                        }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            border: '3px solid rgba(226, 232, 240, 0.3)',
                            borderTop: '3px solid #e2e8f0',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }} />
                          <Typography style={{
                            fontSize: '1rem',
                            fontWeight: 600,
                            textAlign: 'center'
                          }}>
                            Loading camera feed...
                          </Typography>
                        </div>
                      </div>
                    )}
                    
                    {/* Camera Delayed Warning Overlay - Top Left */}
                    {cameraOfflineWarning && (
                      <div style={{
                        position: 'absolute',
                        top: '16px',
                        left: '16px',
                        background: 'rgba(245, 158, 11, 0.95)',
                        color: 'white',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(245, 158, 11, 0.3)',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)'
                      }}>
                        <ErrorOutline style={{ fontSize: '16px' }} />
                        Feed Delayed
                      </div>
                    )}
                    
                    {/* Historical Representative Data Indicator */}
                    {(() => {
                      const displayData = getCurrentDisplayData();
                      const isHistorical = displayData.isHistorical;
                      const currentImg = displayData.cameraData[currentImageIndex];
                      const isRepresentative = currentImg?.isHistoricalRepresentative;
                      
                      return isHistorical && isRepresentative && (
                        <div style={{
                          position: 'absolute',
                          top: '16px',
                          left: cameraOfflineWarning ? '16px' : '16px',
                          marginTop: cameraOfflineWarning ? '50px' : '0px',
                          background: 'rgba(59, 130, 246, 0.95)',
                          color: 'white',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(59, 130, 246, 0.3)',
                          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
                          zIndex: 10
                        }}>
                          <Timeline style={{ fontSize: '16px' }} />
                          Representative Image
                        </div>
                      );
                    })()}
                    
                    {/* Loading overlay */}
                    {imageLoading && (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'rgba(0, 0, 0, 0.8)',
                        color: '#f8fafc',
                        padding: '16px 24px',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(148, 163, 184, 0.3)'
                      }}>
                        <div style={{
                          width: '20px',
                          height: '20px',
                          border: '2px solid rgba(248, 250, 252, 0.3)',
                          borderTop: '2px solid #f8fafc',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        Loading image...
                      </div>
                    )}
                    
                    {/* Time change loading overlay */}
                    {isImageLoadingForTime && (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'rgba(14, 165, 233, 0.9)',
                        color: '#ffffff',
                        padding: '20px 28px',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '1rem',
                        fontWeight: 600,
                        backdropFilter: 'blur(10px)',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 8px 32px rgba(14, 165, 233, 0.4)',
                        zIndex: 20
                      }}>
                        <div style={{
                          width: '24px',
                          height: '24px',
                          border: '3px solid rgba(255, 255, 255, 0.3)',
                          borderTop: '3px solid #ffffff',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        Loading historical data...
                      </div>
                    )}
                  </>
                )}

                {/* Fallback when no images available */}
                {selectedStupa.id !== 'home' && (!currentImage || !getImageSource(currentImage) || cameraImages.length === 0) && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#94a3b8',
                    textAlign: 'center',
                    padding: '40px'
                  }}>
                    <PhotoCamera style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                    <Typography style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '8px' }}>
                      {cameraImages.length === 0 ? 'No Camera Data Available' : 
                       imageError ? 'Camera Feed Unavailable' : 
                       getCurrentDisplayData().isHistorical ? 'No Image Available' : 'No Camera Data'}
                    </Typography>
                    <Typography style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                      {cameraImages.length === 0 ? 
                         `Camera data not available for ${selectedStupa.name}` : 
                       imageError ? 'Unable to load camera feed' : 
                       getCurrentDisplayData().isHistorical ? 
                         `No camera data available for ${currentViewDate.toLocaleDateString()}` : 
                         'Camera data not available for this location'}
                    </Typography>
                  </div>
                )}

                {/* Image Navigation Controls */}
                {selectedStupa.id !== 'home' && (weeklyImages.length > 1 || cameraOfflineWarning) && (
                  <>
                    <IconButton
                      onClick={prevImage}
                      style={{
                        position: 'absolute',
                        left: '16px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'rgba(0, 0, 0, 0.8)',
                        color: '#f8fafc',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        backdropFilter: 'blur(10px)',
                        width: '48px',
                        height: '48px'
                      }}
                    >
                      <ArrowBackIos style={{ fontSize: '20px' }} />
                    </IconButton>
                    <IconButton
                      onClick={nextImage}
                      style={{
                        position: 'absolute',
                        right: '16px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'rgba(0, 0, 0, 0.8)',
                        color: '#f8fafc',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        backdropFilter: 'blur(10px)',
                        width: '48px',
                        height: '48px'
                      }}
                    >
                      <ArrowForwardIos style={{ fontSize: '20px' }} />
                    </IconButton>
                    
                    {/* Week indicator */}
                    {weeklyImages.length > 1 && (
                      <div style={{
                        position: 'absolute',
                        bottom: '16px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'rgba(0, 0, 0, 0.8)',
                        color: '#f8fafc',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(148, 163, 184, 0.3)'
                      }}>
                        Week {currentWeekIndex + 1} of {weeklyImages.length}
                      </div>
                    )}
                  </>
                )}
                
                {/* Camera Gallery Button */}
                <Button
                  onClick={openCameraGallery}
                  style={{
                    position: 'absolute',
                    top: '16px',
                    left: '16px',
                    background: 'rgba(0, 0, 0, 0.8)',
                    color: '#f8fafc',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(148, 163, 184, 0.3)',
                    textTransform: 'none'
                  }}
                  startIcon={<PhotoCamera style={{ fontSize: '16px' }} />}
                >
                  View Images
                </Button>

                {/* Fullscreen Button */}
                {(currentImage || (selectedStupa.id === 'home' && (
                  cameraImages[0] && getImageSource(cameraImages[0])
                ))) && (
                  <Button
                    onClick={() => setFullscreenImageOpen(true)}
                    style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      background: 'rgba(0, 0, 0, 0.8)',
                      color: '#f8fafc',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(148, 163, 184, 0.3)',
                      textTransform: 'none'
                    }}
                    startIcon={<Fullscreen style={{ fontSize: '16px' }} />}
                  >
                    Fullscreen
                  </Button>
                )}
                
                {/* Location and Time Info */}
                <div style={{
                  position: 'absolute',
                  bottom: '0',
                  left: '0',
                  right: '0',
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.9))',
                  padding: '60px 24px 24px',
                  color: '#f8fafc',
                  borderRadius: '0 0 14px 14px'
                }}>
                  {selectedStupa.id === 'home' ? (
                    <>
                      <Typography style={{
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        marginBottom: '8px',
                        textShadow: '0 2px 4px rgba(0,0,0,0.8)'
                      }}>
                        {sponsorConfig.stupaIds[0]?.name || 'All Stupas Overview'}
                      </Typography>
                      <Typography style={{
                        fontSize: '0.9rem',
                        opacity: 0.9,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                        marginBottom: '8px'
                      }}>
                        <LocationOn style={{ fontSize: '18px' }} />
                        Cajon de Maipo, Chile • Representative View
                      </Typography>
                      <Typography style={{
                        fontSize: '0.8rem',
                        color: '#94a3b8',
                        textShadow: '0 1px 2px rgba(0,0,0,0.8)'
                      }}>
                        Home dashboard showing the selected camera feed
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography style={{
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        marginBottom: '8px',
                        textShadow: '0 2px 4px rgba(0,0,0,0.8)'
                      }}>
                        {selectedStupa.name}
                      </Typography>
                      <Typography style={{
                        fontSize: '0.9rem',
                        opacity: 0.9,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                        marginBottom: '8px'
                      }}>
                        <LocationOn style={{ fontSize: '18px' }} />
                        Cajon de Maipo, Chile • {stupaData?.stupa_info?.lat?.toFixed(4)}°, {stupaData?.stupa_info?.lon?.toFixed(4)}°
                      </Typography>
                      {currentImage?.timestamp && (
                        <Typography style={{
                          fontSize: '0.8rem',
                          color: '#94a3b8',
                          textShadow: '0 1px 2px rgba(0,0,0,0.8)'
                        }}>
                          {(() => {
                            const displayData = getCurrentDisplayData();
                            const isHistorical = displayData.isHistorical;
                            const isRepresentative = currentImage?.isHistoricalRepresentative;
                            
                            if (isHistorical && isRepresentative) {
                              return `Representative image for: ${currentViewDate.toLocaleDateString('en-US', { 
                                weekday: 'short', 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })}`;
                            } else {
                              return `Image from: ${new Date(currentImage.timestamp).toLocaleString()}`;
                            }
                          })()}
                        </Typography>
                      )}
                    </>
                  )}
                  
                  {/* Data timestamp disclaimer */}
                  {dataTimestamp && (
                    <Typography style={{
                      fontSize: '0.75rem',
                      color: '#64748b',
                      textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                      marginTop: '4px',
                      fontStyle: 'italic'
                    }}>
                      Replenishment data through: {dataTimestamp.toLocaleDateString()}
                    </Typography>
                  )}
                  
                  {/* Historical Date Slider - REMOVED */}
                </div>
              </div>
              
              {/* System Status Indicators - Bottom 15% of Camera Card */}
              <div style={{
                height: '15%', // Reduced from 25% since we removed the slider
                padding: '8px 16px', // Reduced padding to be more compact
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Typography variant="h6" style={{
                  fontWeight: 600,
                  color: '#f8fafc',
                  marginBottom: '8px', // Reduced from 12px
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '0.8rem' // Slightly smaller
                }}>
                  <Assessment style={{ fontSize: '12px', color: '#0ea5e9' }} />
                  System Status
                </Typography>
                
                {(() => {
                  const statusData = getOperationalStatus();
                  return (
                    <div style={{ display: 'flex', gap: '4px' }}> {/* Further reduced gap */}
                      {/* Glacier Status */}
                      <div 
                        style={{
                          background: 'rgba(16, 185, 129, 0.15)',
                          border: `1px solid ${statusData.glacier.color}`,
                          borderRadius: '4px', // Reduced from 6px
                          padding: '12px 8px', // Increased padding for more height
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px', // Reduced gap
                          flex: 1,
                          minHeight: '48px', // Increased height further
                          position: 'relative',
                          cursor: 'help'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.setAttribute('title', statusData.glacier.description);
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.removeAttribute('title');
                        }}
                      >
                        <Circle style={{ 
                          fontSize: '5px', // Smaller circle
                          color: statusData.glacier.color,
                          animation: 'pulse 2s ease-in-out infinite'
                        }} />
                        <div>
                          <Typography style={{
                            fontSize: '0.65rem', // Reduced font size
                            fontWeight: 600,
                            color: '#f8fafc',
                            lineHeight: 1.2, // Increased line height
                            marginBottom: '2px' // Added margin between lines
                          }}>
                            🏔️ {statusData.glacier.status}
                          </Typography>
                          <Typography style={{
                            fontSize: '0.55rem', // Reduced font size
                            color: '#94a3b8',
                            lineHeight: 1.2 // Increased line height
                          }}>
                            Glacier System
                          </Typography>
                        </div>
                      </div>
                      
                      {/* Camera Status */}
                      <div 
                        style={{
                          background: `rgba(${statusData.camera.color === '#10b981' ? '16, 185, 129' : '245, 158, 11'}, 0.15)`,
                          border: `1px solid ${statusData.camera.color}`,
                          borderRadius: '4px', // Reduced from 6px
                          padding: '12px 8px', // Increased padding for more height
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px', // Reduced gap
                          flex: 1,
                          minHeight: '48px', // Increased height further
                          position: 'relative',
                          cursor: 'help'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.setAttribute('title', statusData.camera.description);
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.removeAttribute('title');
                        }}
                      >
                        <Circle style={{ 
                          fontSize: '5px', // Smaller circle
                          color: statusData.camera.color
                        }} />
                        <div>
                          <Typography style={{
                            fontSize: '0.65rem', // Reduced font size
                            fontWeight: 600,
                            color: '#f8fafc',
                            lineHeight: 1.2, // Increased line height
                            marginBottom: '2px' // Added margin between lines
                          }}>
                            📷 {statusData.camera.status}
                          </Typography>
                          <Typography style={{
                            fontSize: '0.55rem', // Reduced font size
                            color: '#94a3b8',
                            lineHeight: 1.2 // Increased line height
                          }}>
                            Camera Feed
                          </Typography>
                        </div>
                      </div>
                      
                      {/* Data Flow Status */}
                      <div 
                        style={{
                          background: `rgba(${statusData.data.color === '#10b981' ? '16, 185, 129' : '245, 158, 11'}, 0.15)`,
                          border: `1px solid ${statusData.data.color}`,
                          borderRadius: '4px', // Reduced from 6px
                          padding: '12px 8px', // Increased padding for more height
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px', // Reduced gap
                          flex: 1,
                          minHeight: '48px', // Increased height further
                          position: 'relative',
                          cursor: 'help'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.setAttribute('title', statusData.data.description);
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.removeAttribute('title');
                        }}
                      >
                        <Circle style={{ 
                          fontSize: '5px', // Smaller circle
                          color: statusData.data.color
                        }} />
                        <div>
                          <Typography style={{
                            fontSize: '0.65rem', // Reduced font size
                            fontWeight: 600,
                            color: '#f8fafc',
                            lineHeight: 1.2, // Increased line height
                            marginBottom: '2px' // Added margin between lines
                          }}>
                            📊 {statusData.data.status}
                          </Typography>
                          <Typography style={{
                            fontSize: '0.55rem', // Reduced font size
                            color: '#94a3b8',
                            lineHeight: 1.2 // Increased line height
                          }}>
                            Data Flow
                          </Typography>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </Card>
          </Grid>

          {/* Right Column - Professional Metrics Dashboard */}
          <Grid item xs={12} lg={7}>
            
            {/* Current-season goal progress bar */}
            <div style={{
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              borderRadius: '8px',
              border: '1px solid rgba(148, 163, 184, 0.2)',
              padding: '12px 16px',
              marginBottom: '16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Subtle background accent */}
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '80px',
                height: '100%',
                background: 'linear-gradient(135deg, rgba(220, 38, 127, 0.02) 0%, rgba(239, 68, 68, 0.02) 100%)',
                borderRadius: '50% 0 0 50%'
              }} />
              
              <div style={{ position: 'relative', zIndex: 2 }}>
                {/* Header row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <WaterDrop style={{ fontSize: '14px', color: 'white' }} />
                    </div>
                    <Typography style={{
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      color: '#f8fafc'
                    }}>
                      {sponsorConfig.seasonYear || 'Current'} Goal:{' '}
                      {sponsorConfig.goal !== undefined
                        ? `${sponsorConfig.goal}m³`
                        : 'Pending'}
                    </Typography>
                  </div>
                  
                  {/* Compact status badge */}
                  {(() => {
                    // Use original total replenished (locked to page load) instead of current display data
                    const currentTotal = originalTotalReplenished || 0;
                    const goalTarget = sponsorConfig.goal;
                    if (!goalTarget || goalTarget <= 0) {
                      return (
                        <div style={{
                          background: '#64748b',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontSize: '0.6rem',
                          fontWeight: 700
                        }}>
                          GOAL PENDING
                        </div>
                      );
                    }
                    const progressPercentage = (currentTotal / goalTarget) * 100;
                    
                    if (progressPercentage >= 100) {
                      return (
                        <div style={{
                          background: '#10b981',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontSize: '0.6rem',
                          fontWeight: 700
                        }}>
                          🎯 ACHIEVED
                        </div>
                      );
                    } else if (progressPercentage >= 75) {
                      return (
                        <div style={{
                          background: '#f59e0b',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontSize: '0.6rem',
                          fontWeight: 700
                        }}>
                          🔥 ON TRACK
                        </div>
                      );
                    } else {
                      return (
                        <div style={{
                          background: '#0ea5e9',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontSize: '0.6rem',
                          fontWeight: 700
                        }}>
                          📈 PROGRESS
                        </div>
                      );
                    }
                  })()}
                </div>
                
                {/* Progress data and bar */}
                {(() => {
                  // Use original total replenished (locked to page load) instead of current display data
                  const currentTotal = originalTotalReplenished || 0;
                  const goalTarget = sponsorConfig.goal;
                  if (!goalTarget || goalTarget <= 0) {
                    return (
                      <Typography style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                        Goal target has not been configured.
                      </Typography>
                    );
                  }
                  const progressPercentage = (currentTotal / goalTarget) * 100;
                  
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          background: 'rgba(15, 23, 42, 0.5)',
                          borderRadius: '4px',
                          padding: '2px',
                          border: '1px solid rgba(148, 163, 184, 0.1)'
                        }}>
                          <div style={{
                            height: '6px',
                            background: progressPercentage >= 100 
                              ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
                              : progressPercentage >= 75
                                ? 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)'
                                : 'linear-gradient(90deg, #0ea5e9 0%, #0284c7 100%)',
                            borderRadius: '2px',
                            width: `${Math.min(progressPercentage, 100)}%`,
                            transition: 'width 0.8s ease-out'
                          }} />
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '120px' }}>
                        <Typography style={{
                          fontSize: '0.9rem',
                          fontWeight: 700,
                          color: '#f8fafc',
                          fontFamily: 'monospace'
                        }}>
                          {Math.round(currentTotal)}m³
                        </Typography>
                        <Typography style={{
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: progressPercentage >= 100 ? '#10b981' : '#0ea5e9'
                        }}>
                          ({progressPercentage.toFixed(1)}%)
                        </Typography>
                        {progressPercentage > 100 && (
                          <Typography style={{
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            color: '#10b981',
                            background: 'rgba(16, 185, 129, 0.2)',
                            padding: '1px 4px',
                            borderRadius: '4px'
                          }}>
                            +{(progressPercentage - 100).toFixed(1)}%
                          </Typography>
                        )}
                      </div>
                    </div>
                  );
                })()}
                
                {/* Date disclaimer for progress */}
                {originalDataTimestamp && (
                  <div style={{ 
                    fontSize: '0.6rem', 
                    color: '#64748b', 
                    textAlign: 'center',
                    marginTop: '4px',
                    fontStyle: 'italic'
                  }}>
                    Progress as of {originalDataTimestamp.toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
            
            {/* Key Metrics Row */}
            <Grid container spacing={2} style={{ marginBottom: '24px' }}> {/* Reduced spacing and margin */}
              {/* Volume Managed tile removed */}
              
              <Grid item xs={12} sm={6} md={4}>
                <Card style={{
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  padding: '20px', // Reduced from 24px
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
                }}>
                  <WaterDrop style={{ fontSize: '28px', color: '#0ea5e9', marginBottom: '10px' }} />
                  <Typography variant="h4" style={{
                    fontWeight: 700,
                    color: '#f8fafc',
                    marginBottom: '2px', // Reduced from 4px
                    fontFamily: 'monospace',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '1.8rem' // Slightly smaller
                  }}>
                    {(() => {
                      if (isImageLoadingForTime || isLoadingHistorical) {
                        return <LoadingCard>Loading total data...</LoadingCard>;
                      }
                      if (selectedStupa.id === 'home') {
                        return Math.round(originalTotalReplenished);
                      }
                      const displayData = getCurrentDisplayData();
                      const totalReplenished = displayData.waterData?.total_replenished;
                      if (totalReplenished !== undefined && totalReplenished !== null) {
                        return Math.round(totalReplenished);
                      } else {
                        return (
                          <>
                            <ErrorOutline style={{ fontSize: '20px', color: '#ef4444' }} />
                            <span style={{ fontSize: '0.7rem', color: '#ef4444' }}>Error</span>
                          </>
                        );
                      }
                    })()}
                  </Typography>
                  <Typography style={{
                    fontSize: '0.8rem', // Reduced from 0.875rem
                    color: '#94a3b8',
                    fontWeight: 600
                  }}>
                    m³ Total Replenished
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Card style={{
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  padding: '20px', // Reduced from 24px
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
                }}>
                  <Timeline style={{ fontSize: '28px', color: '#8b5cf6', marginBottom: '10px' }} />
                  <Typography variant="h4" style={{
                    fontWeight: 700,
                    color: '#f8fafc',
                    marginBottom: '2px', // Reduced from 4px
                    fontFamily: 'monospace',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '1.8rem' // Slightly smaller
                  }}>
                    {(() => {
                      // Show loading when historical data is being fetched
                      if (isImageLoadingForTime || isLoadingHistorical) {
                        return <LoadingCard>Loading avg data...</LoadingCard>;
                      }
                      
                      const displayData = getCurrentDisplayData();
                      const avgVolumePerMonth = Number(
                        displayData.waterData?.average_monthly_replenished || 0
                      );
                      
                      return avgVolumePerMonth.toFixed(0);
                    })()}
                  </Typography>
                  <Typography style={{
                    fontSize: '0.8rem', // Reduced from 0.875rem
                    color: '#94a3b8',
                    fontWeight: 600
                  }}>
                    {selectedStupa.id === 'home'
                      ? 'Avg Growth m³/Month/Stupa'
                      : 'Avg Growth m³/Month'}
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Card style={{
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  padding: '20px', // Reduced from 24px
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
                }}>
                  <AcUnit style={{ fontSize: '28px', color: '#06b6d4', marginBottom: '10px' }} />
                  <Typography variant="h4" style={{
                    fontWeight: 700,
                    color: '#f8fafc',
                    marginBottom: '2px', // Reduced from 4px
                    fontFamily: 'monospace',
                    fontSize: '1.8rem' // Slightly smaller
                  }}>
                    {(() => {
                      // Show loading when historical data is being fetched
                      if (isImageLoadingForTime || isLoadingHistorical) {
                        return <LoadingCard>Loading freezing data...</LoadingCard>;
                      }
                      
                      const displayData = getCurrentDisplayData();
                      const freezingStats = displayData.stupaData?.freezing_stats;
                      const harvestedHours = freezingStats?.harvested_freezing_hours;
                      const formatted = formatHarvestedFreezingTime(harvestedHours);
                      return (
                        <>
                          <span>{formatted.primary}</span>
                          {formatted.detail && (
                            <span style={{
                              display: 'block',
                              marginTop: '2px',
                              color: '#94a3b8',
                              fontFamily: 'Arial, sans-serif',
                              fontSize: '0.7rem'
                            }}>
                              {formatted.detail}
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </Typography>
                  <Typography style={{
                    fontSize: '0.8rem', // Reduced from 0.875rem
                    color: '#94a3b8',
                    fontWeight: 600
                  }}>
                    Harvested Freezing Time
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            {/* Professional Analytics Card */}
            <Card style={{
              borderRadius: '16px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(148, 163, 184, 0.1)',
              border: '1px solid rgba(148, 163, 184, 0.2)',
              marginBottom: '24px', // Reduced from 32px
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
            }}>
              <CardContent style={{ padding: '28px' }}> {/* Reduced from 36px */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}> {/* Reduced from 32px */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Assessment style={{ fontSize: '24px', color: '#0ea5e9' }} /> {/* Reduced from 28px */}
                    <Typography variant="h6" style={{
                      fontWeight: 700,
                      color: '#f8fafc',
                      fontSize: '1.2rem' // Reduced from 1.35rem
                    }}>
                      Performance Analytics
                    </Typography>
                  </div>
                  <button
                    style={{
                      background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(14, 165, 233, 0.3)',
                      transition: 'all 0.2s ease',
                      marginLeft: 'auto'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(14, 165, 233, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(14, 165, 233, 0.3)';
                    }}
                  >
                    6-Month Analysis
                  </button>
                </div>

                {/* Water Replenishment Chart - The Key Metric */}
                <div style={{ marginBottom: '24px' }}> {/* Reduced from 32px */}
                  {/* Section header for home page */}
                  {selectedStupa.id === 'home' && (
                    <div style={{
                      border: '3px solid #10b981',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      marginBottom: '20px',
                      background: 'rgba(16, 185, 129, 0.05)'
                    }}>
                      <Typography style={{
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: '#f8fafc',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <AcUnit style={{ fontSize: '16px', color: '#10b981' }} />
                        Individual Glacier Performance Summary:
                      </Typography>
                    </div>
                  )}
                  
                  {/* Chart title for individual stupa pages */}
                  {selectedStupa.id !== 'home' && (
                    <Typography style={{
                      fontSize: '1.1rem',
                      color: '#e2e8f0',
                      marginBottom: '24px',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <WaterDrop style={{ fontSize: '20px', color: '#0ea5e9' }} />
                      Water Replenishment - 6 Month Performance
                    </Typography>
                  )}
                  
                  <div style={{ 
                    display: 'flex', 
                    gap: '20px', 
                    alignItems: 'end', 
                    height: '220px', // Increased from 180px to accommodate labels
                    marginBottom: '24px', // Reduced from 30px
                    padding: '16px', // Reduced from 20px
                    background: 'rgba(15, 23, 42, 0.3)',
                    borderRadius: '12px',
                    border: '1px solid rgba(148, 163, 184, 0.1)',
                    position: 'relative'
                  }}
                  key={`chart-${currentViewDate.toISOString().split('T')[0]}`} // Force re-render when date changes
                  >
                    {/* Loading overlay for chart updates */}
                    {(isLoadingHistorical || isImageLoadingForTime) && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(15, 23, 42, 0.9)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10,
                        backdropFilter: 'blur(2px)'
                      }}>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '12px',
                          color: '#0ea5e9',
                          fontSize: '0.9rem',
                          fontWeight: 600
                        }}>
                          <LoadingSpinner size="24px" />
                          <div>
                            {isImageLoadingForTime ? 'Loading historical data...' : 'Updating chart...'}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {(() => {
                      const displayData = getCurrentDisplayData();
                      const data = displayData.waterData?.monthly_replenished || [0, 0, 0, 0, 0, 0];
                      const dateRanges = displayData.stupaData?.replenishment_stats?.monthly_date_ranges || [];
                      const maxVolume = Math.max(...data.map((val: number) => Math.max(0, val)), 1); // Clamp negative values to 0 for max calculation
                      const { weekLabels } = getWeekRangeForDisplay(currentViewDate);
                      const selectedWeekNumber = getWeeksSinceDeployment(currentViewDate);
                      
                      // Debug logging
                      console.log('Bar Chart Data:', { data, maxVolume, selectedStupa: selectedStupa.id });
                      
                      if (selectedStupa.id === 'home') {
                        // Individual Stupa Performance Summary - Replace bar chart on home page
                        return (
                          <div style={{
                            display: 'flex',
                            gap: '16px',
                            height: '100%',
                            alignItems: 'stretch',
                            width: '100%'
                          }}>
                            {sponsorConfig.stupaIds.map((stupa) => {
                              // Get data for this specific stupa
                              const stupaData = waterDataMap[stupa.id];
                                            // Show actual glacier performance (100%) in tiles
              const actualTotalReplenished = stupaData?.total_replenished || 0;
              const actualLast30DaysReplenished = stupaData?.last_30_days_replenished || 0;
                              const cumulativeStats = getCumulativeStats();
                              const weeklyReplenished = cumulativeStats.currentWeekReplenishedByStupa[stupa.id] || 0;
                              const isActive = stupaData && stupaData.total_replenished > 0;
                              
                              return (
                                <div
                                  key={stupa.id}
                                  onClick={() => handleStupaSwitch(stupa)}
                                  style={{
                                    flex: 1,
                                    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                                    borderRadius: '12px',
                                    padding: '20px',
                                    border: '2px solid rgba(148, 163, 184, 0.2)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    minHeight: '140px',
                                    position: 'relative',
                                    overflow: 'hidden'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(14, 165, 233, 0.3)';
                                    e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.5)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                                    e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.2)';
                                  }}
                                >
                                  {/* Subtle background accent */}
                                  <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    width: '60px',
                                    height: '100%',
                                    background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.05) 0%, rgba(16, 185, 129, 0.05) 100%)',
                                    borderRadius: '50% 0 0 50%'
                                  }} />
                                  
                                  <div style={{ position: 'relative', zIndex: 2 }}>
                                    {/* Stupa name */}
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      marginBottom: '16px'
                                    }}>
                                      <Typography style={{
                                        fontSize: '1.1rem',
                                        fontWeight: 700,
                                        color: '#0ea5e9',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                      }}>
                                        <AcUnit style={{ fontSize: '18px' }} />
                                        {stupa.name}
                                      </Typography>
                                    </div>
                                    
                                    {/* Data Grid */}
                                    <div style={{
                                      display: 'grid',
                                      gridTemplateColumns: '1fr 1fr',
                                      gap: '12px',
                                      marginBottom: '12px'
                                    }}>
                                      <div>
                                        <Typography style={{
                                          fontSize: '0.75rem',
                                          color: '#94a3b8',
                                          marginBottom: '4px'
                                        }}>
                                          Total:
                                        </Typography>
                                        <Typography style={{
                                          fontSize: '1rem',
                                          fontWeight: 600,
                                          color: '#f8fafc'
                                        }}>
                                          {formatGlacierTotalVolume(actualTotalReplenished)}
                                        </Typography>
                                      </div>
                                      
                                      <div>
                                        <Typography style={{
                                          fontSize: '0.75rem',
                                          color: '#94a3b8',
                                          marginBottom: '4px'
                                        }}>
                                          Last 30 Days:
                                        </Typography>
                                        <Typography style={{
                                          fontSize: '1rem',
                                          fontWeight: 600,
                                          color: '#f8fafc'
                                        }}>
                                          {formatLast30DaysVolume(actualLast30DaysReplenished)}
                                        </Typography>
                                      </div>
                                    </div>
                                    
                                    {/* Status */}
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px'
                                    }}>
                                      <Typography style={{
                                        fontSize: '0.75rem',
                                        color: '#94a3b8'
                                      }}>
                                        Status:
                                      </Typography>
                                      <Typography style={{
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        color: isActive ? '#10b981' : '#f59e0b'
                                      }}>
                                        {isActive ? 'Active' : 'Inactive'}
                                      </Typography>
                                    </div>
                                  </div>
                                  
                                  {/* Click indicator */}
                                  <div style={{
                                    position: 'absolute',
                                    bottom: '8px',
                                    right: '8px',
                                    fontSize: '0.6rem',
                                    color: '#64748b',
                                    opacity: 0.7
                                  }}>
                                    Click to view →
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      } else {
                        // Regular single stupa chart with actual date ranges
                        // Filter out months with zero data and get actual month labels
                        const monthsWithData = data.map((volume: number, index: number) => {
                          const dateRange = dateRanges[index];
                          // Show all months with valid date ranges, regardless of volume
                          if (dateRange && dateRange.start !== 'N/A' && dateRange.end !== 'N/A') {
                            // Fix timezone issue by parsing date more explicitly
                            const [year, month, day] = dateRange.start.split('-').map(Number);
                            const startDate = new Date(year, month - 1, day); // month - 1 because JS months are 0-indexed
                            const monthName = startDate.toLocaleDateString('en-US', { month: 'long' });
                            const yearValue = startDate.getFullYear();
                            return {
                              volume,
                              dateLabel: `${monthName} ${yearValue}`,
                              index
                            };
                          }
                          return null;
                        }).filter(Boolean);
                        
                        // If no data, show a message
                        if (monthsWithData.length === 0) {
                          return (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '200px',
                              color: '#94a3b8',
                              fontSize: '1rem'
                            }}>
                              No replenishment data available for this time period
                            </div>
                          );
                        }
                        
                        // Calculate max volume from actual data
                        const maxVolume = Math.max(...monthsWithData.map((item: any) => item.volume), 1);
                        
                        return monthsWithData.map((item: any, displayIndex: number) => {
                          const { volume, dateLabel, index } = item;
                          const displayVolume = Math.max(0, volume);
                          const heightPixels = (displayVolume / maxVolume) * 120;
                          
                          // Get the month and year from the currentViewDate
                          const selectedMonth = currentViewDate.getMonth() + 1; // getMonth() returns 0-11
                          const selectedYear = currentViewDate.getFullYear();
                          
                          // Get the month and year from the dateRange for this bar
                          const dateRange = dateRanges[index];
                          let barMonth = 0;
                          let barYear = 0;
                          if (dateRange) {
                            // Fix timezone issue by parsing date more explicitly
                            const [year, month, day] = dateRange.start.split('-').map(Number);
                            barMonth = month; // Already 1-indexed from the split
                            barYear = year;
                          }
                          
                          // Check if this bar corresponds to the selected month/year
                          const isSelectedMonth = barMonth === selectedMonth && barYear === selectedYear;
                          
                          // Check if we're viewing today's date and this is the last month (current month)
                          const isViewingToday = currentViewDate.toDateString() === new Date().toDateString();
                          const isCurrentMonth = isViewingToday && index === data.length - 1;
                          
                          // Debug logging
                          console.log('DEBUG:', {
                            dateLabel,
                            selectedMonth,
                            selectedYear,
                            barMonth,
                            barYear,
                            isSelectedMonth,
                            isCurrentMonth,
                            currentViewDate: currentViewDate.toDateString(),
                            today: new Date().toDateString()
                          });
                          
                          return (
                            <div key={index} style={{ 
                              flex: 1, 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center',
                              position: 'relative',
                              height: '220px', // Fixed height for consistent alignment - increased to match container
                              justifyContent: 'flex-end' // Align everything to bottom
                            }}>
                              {/* 3D Bar Effect */}
                              <div style={{
                                position: 'relative',
                                width: '100%',
                                height: `${Math.max(heightPixels, 20)}px`,
                                marginBottom: '16px',
                                transition: 'height 0.6s cubic-bezier(0.4, 0, 0.2, 1)', // Smooth height transition
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-end' // Align bar content to bottom
                              }}>
                                {/* Main bar */}
                                <div
                                  style={{
                                    width: '100%',
                                    height: displayVolume > 0 ? '100%' : '20px', // Ensure minimum height for zero values
                                    background: isSelectedMonth 
                                      ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' // Amber for selected
                                      : isCurrentMonth 
                                        ? 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' // Blue for current
                                        : 'linear-gradient(135deg, #475569 0%, #64748b 100%)', // Gray for others
                                    borderRadius: '12px 12px 6px 6px',
                                    boxShadow: 'none',
                                    position: 'relative',
                                    transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)', // Smooth transition for height changes
                                    border: 'none',
                                    opacity: displayVolume > 0 ? 1 : 0.4 // Dim zero-value bars slightly
                                  }}
                                />
                              </div>
                              
                              <Typography style={{
                                fontSize: '0.9rem',
                                color: isSelectedMonth 
                                  ? '#f59e0b' 
                                  : isCurrentMonth 
                                    ? '#0ea5e9' 
                                    : '#e2e8f0',
                                fontWeight: isSelectedMonth || isCurrentMonth ? 700 : 600,
                                textAlign: 'center',
                                marginBottom: '4px'
                              }}>
                                {displayVolume}m³
                              </Typography>
                              <Typography style={{
                                fontSize: '0.75rem',
                                color: isSelectedMonth ? '#f59e0b' : '#94a3b8',
                                fontWeight: isSelectedMonth ? 600 : 500,
                                textAlign: 'center',
                                marginBottom: '2px'
                              }}>
                                {dateLabel}
                              </Typography>
                              {isCurrentMonth && !isSelectedMonth && (
                                <div style={{
                                  fontSize: '0.65rem',
                                  color: '#0ea5e9',
                                  fontWeight: 700
                                }}>
                                  (Current)
                                </div>
                              )}
                            </div>
                          );
                        });
                      }
                    })()}
                  </div>
                  
                  {/* Legend for stacked bar chart (home view only) - REMOVED */}
                  
                  {/* Total replenished to date section removed - redundant with progress bar */}
                </div>

                <Divider style={{ background: 'rgba(148, 163, 184, 0.2)', margin: '32px 0' }} />

              </CardContent>
            </Card>

            {/* Professional Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px', // Reduced from 16px
              marginTop: '20px', // Reduced from 32px
              flexWrap: 'wrap'
            }}>
              <Button
                variant="contained"
                startIcon={<Assessment />}
                onClick={generateExecutiveReport}
                style={{
                  padding: '12px 24px', // Reduced from 16px 32px
                  fontSize: '0.9rem', // Reduced from 0.95rem
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                  borderRadius: '8px',
                  textTransform: 'none',
                  boxShadow: '0 8px 25px rgba(14, 165, 233, 0.3)',
                  color: 'white'
                }}
              >
                Generate Executive Report
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<CalendarToday />}
                href="mailto:info@nilus.eco?subject=Request%20a%20Site%20Visit"
                style={{
                  padding: '12px 24px', // Reduced from 16px 32px
                  fontSize: '0.9rem', // Reduced from 0.95rem
                  fontWeight: 600,
                  borderColor: '#64748b',
                  color: '#e2e8f0',
                  borderWidth: '2px',
                  borderRadius: '8px',
                  textTransform: 'none',
                  background: 'rgba(100, 116, 139, 0.1)'
                }}
              >
                Request a Site Visit
              </Button>
            </div>
          </Grid>
        </Grid>

        {/* Professional Footer Info */}
        <Card style={{
          borderRadius: '16px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(148, 163, 184, 0.1)',
          border: '1px solid rgba(148, 163, 184, 0.2)',
          marginTop: '40px',
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
        }}>
          <CardContent style={{ padding: '36px' }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h6" style={{
                  fontWeight: 700,
                  color: '#f8fafc',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <AcUnit style={{ fontSize: '24px', color: '#0ea5e9' }} />
                  Enterprise Glacier Monitoring System
                </Typography>
                <Typography style={{
                  color: '#94a3b8',
                  fontSize: '0.95rem',
                  lineHeight: 1.7
                }}>
                  Powered by advanced IoT sensors, satellite connectivity, and AI-driven analytics. 
                  Our monitoring infrastructure provides real-time environmental data with 99.9% uptime, 
                  ensuring continuous oversight of your water preservation investment. All data is 
                  validated through independent environmental auditing protocols.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4} style={{ textAlign: 'center' }}>
                <Typography style={{
                  fontSize: '0.75rem',
                  color: '#64748b',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontWeight: 600
                }}>
                  System Deployment
                </Typography>
                <Typography style={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: '#f8fafc'
                }}>
                  {stupaDeploymentDate.toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric',
                  })}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

      </Container>
      
      {/* Camera Gallery Modal */}
      <Dialog 
        open={cameraGalleryOpen} 
        onClose={() => setCameraGalleryOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle style={{ background: '#1e293b', color: '#f8fafc', borderBottom: '1px solid #334155' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Typography variant="h6">Camera Gallery</Typography>
              {sponsorConfig.cameras.length > 0 && (
                <select
                  aria-label="Gallery camera"
                  value={selectedCameraId}
                  onChange={async (event) => {
                    const cameraId = event.target.value;
                    setSelectedCameraId(cameraId);
                    await loadGalleryForCamera(
                      cameraId,
                      1,
                      selectedDate || undefined,
                    );
                  }}
                  style={{ minWidth: 140, padding: '6px 10px' }}
                >
                  {sponsorConfig.cameras.map((cameraId) => (
                    <option key={cameraId} value={cameraId}>{cameraId}</option>
                  ))}
                </select>
              )}
            </div>
            <Typography style={{ fontSize: '0.9rem', color: '#94a3b8' }}>
              {galleryTotal} images available
            </Typography>
          </div>
        </DialogTitle>
        <DialogContent style={{ background: '#1e293b', padding: '20px' }}>
          {/* Loading indicator - only show if no images are available yet */}
          {galleryLoading && galleryImages.length === 0 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              padding: '40px',
              color: '#94a3b8' 
            }}>
              <div style={{
                width: '24px',
                height: '24px',
                border: '3px solid rgba(148, 163, 184, 0.3)',
                borderTop: '3px solid #0ea5e9',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginRight: '12px'
              }} />
              Loading images...
            </div>
          )}

          {/* Loading more indicator - show when we have images but loading more */}
          {galleryLoading && galleryImages.length > 0 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              padding: '16px',
              color: '#94a3b8',
              borderBottom: '1px solid #334155',
              marginBottom: '16px'
            }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid rgba(148, 163, 184, 0.3)',
                borderTop: '2px solid #0ea5e9',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginRight: '8px'
              }} />
              Loading additional images...
            </div>
          )}

          {/* Images Grid */}
          {galleryImages.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '20px',
              maxHeight: '600px',
              overflowY: 'auto',
              padding: '8px'
            }}>
              {galleryImages.map((image, index) => {
                const imageKey = galleryImageKey(image);
                const shouldLoad = index === 0 || loadedGalleryImages.has(imageKey);
                return (
                <div
                  key={imageKey}
                  style={{
                    border: '1px solid #64748b',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    background: '#334155',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                  }}
                >
                  <div style={{ position: 'relative', height: '180px' }}>
                    <AuthenticatedImage
                      src={getImageSource(image)}
                      alt={`Gallery image ${((galleryPage - 1) * 8) + index + 1}`}
                      enabled={shouldLoad}
                      style={{
                        width: '100%',
                        height: '180px',
                        objectFit: 'cover'
                      }}
                    />
                    {!shouldLoad && (
                      <Button
                        variant="contained"
                        onClick={() => setLoadedGalleryImages((current) => {
                          const next = new Set(current);
                          next.add(imageKey);
                          return next;
                        })}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          textTransform: 'none',
                        }}
                      >
                        Load image
                      </Button>
                    )}
                  </div>
                  <div style={{ padding: '16px' }}>
                    <Typography style={{
                      fontSize: '0.9rem',
                      color: '#e2e8f0',
                      textAlign: 'center',
                      fontWeight: 600,
                      marginBottom: '8px'
                    }}>
                      {new Date(image.timestamp).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </Typography>
                    <Typography style={{
                      fontSize: '0.8rem',
                      color: '#94a3b8',
                      textAlign: 'center'
                    }}>
                      {new Date(image.timestamp).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit',
                        hour12: true
                      })}
                    </Typography>
                  </div>
                </div>
              )})}
            </div>
          ) : !galleryLoading && galleryImages.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: '#94a3b8'
            }}>
              <PhotoCamera style={{ fontSize: '64px', marginBottom: '20px', opacity: 0.3 }} />
              <Typography style={{ fontSize: '1.2rem', marginBottom: '12px', fontWeight: 600 }}>
                No images available
              </Typography>
              <Typography style={{ fontSize: '1rem', opacity: 0.8, lineHeight: 1.5 }}>
                Images will appear here once they are available in the images collection.
              </Typography>
            </div>
          )}

          {galleryTotal > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              paddingTop: '20px',
            }}>
              <Button
                variant="outlined"
                disabled={galleryLoading || galleryPage <= 1}
                onClick={() => loadGalleryForCamera(
                  selectedCameraId,
                  galleryPage - 1,
                  selectedDate || undefined,
                )}
              >
                Previous
              </Button>
              <Typography style={{ color: '#e2e8f0' }}>
                Page {galleryPage} of {Math.max(1, Math.ceil(galleryTotal / 8))}
              </Typography>
              <Button
                variant="outlined"
                disabled={
                  galleryLoading
                  || galleryPage >= Math.ceil(galleryTotal / 8)
                }
                onClick={() => loadGalleryForCamera(
                  selectedCameraId,
                  galleryPage + 1,
                  selectedDate || undefined,
                )}
              >
                Next
              </Button>
            </div>
          )}
        </DialogContent>
        <DialogActions style={{ background: '#1e293b', borderTop: '1px solid #334155' }}>
          <Button onClick={() => setCameraGalleryOpen(false)} style={{ color: '#e2e8f0' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Executive Report Dialog */}
      <Dialog 
        open={executiveReportOpen} 
        onClose={() => setExecutiveReportOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle style={{ background: '#1e293b', color: '#f8fafc' }}>
          Generate Executive Report
        </DialogTitle>
        <DialogContent style={{ background: '#1e293b', padding: '20px' }}>
          <Typography style={{ color: '#e2e8f0', marginBottom: '16px' }}>
            This report will include:
          </Typography>
          <ul style={{ color: '#94a3b8', paddingLeft: '20px', lineHeight: 1.6 }}>
            <li>Executive summary for partners and stakeholders</li>
            <li>Weekly detailed performance metrics</li>
            <li>Historical freezing times and replenishment statistics</li>
            <li>Environmental conditions and sensor data</li>
            <li>Camera status and recent imagery information</li>
            <li>Data quality assessments and recommendations</li>
          </ul>
          <Typography style={{ 
            color: '#64748b', 
            fontSize: '0.85rem', 
            marginTop: '16px',
            fontStyle: 'italic'
          }}>
            Export the same company-scoped report as a formatted PDF or a structured XLSX spreadsheet.
          </Typography>
          {reportExportError && (
            <Typography color="error" sx={{ mt: 2 }}>
              {reportExportError}
            </Typography>
          )}
          <div style={{ marginTop: 24, overflowX: 'auto', userSelect: 'text' }}>
            <ProfessionalExecutiveReport
              clientSlug={sponsorSlug}
              clientName={sponsorConfig.name}
              reportRef={reportRef}
              onReportReady={() => {
                reportReadyRef.current = true;
              }}
              onReportDataReady={setExecutiveReportModel}
              clientLogo={logoUrl}
              apiBase={companyApiBase}
            />
          </div>
        </DialogContent>
        <DialogActions style={{ background: '#1e293b' }}>
          <Button onClick={() => setExecutiveReportOpen(false)} style={{ color: '#e2e8f0' }}>
            Cancel
          </Button>
          <Button
            onClick={downloadExecutiveSpreadsheet}
            variant="outlined"
            disabled={!executiveReportModel || spreadsheetGenerating}
            startIcon={<Download />}
            sx={{ color: '#e2e8f0', borderColor: '#64748b' }}
          >
            {spreadsheetGenerating ? 'Exporting XLSX...' : 'Export XLSX'}
          </Button>
          <Button 
            onClick={downloadExecutiveReport}
            variant="contained"
            disabled={reportGenerating}
            startIcon={reportGenerating ? (
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            ) : <Download />}
            style={{
              background: reportGenerating 
                ? 'linear-gradient(135deg, #64748b 0%, #475569 100%)' 
                : 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
              color: 'white',
              opacity: reportGenerating ? 0.7 : 1,
              cursor: reportGenerating ? 'not-allowed' : 'pointer'
            }}
          >
            {reportGenerating ? 'Generating PDF...' : 'Export PDF'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen Image Modal */}
      <Dialog 
        open={fullscreenImageOpen} 
        onClose={() => setFullscreenImageOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          style: {
            background: 'rgba(0, 0, 0, 0.95)',
            margin: '16px',
            maxWidth: '95vw',
            maxHeight: '95vh',
            width: '95vw',
            height: '95vh'
          }
        }}
      >
        <DialogContent style={{ 
          padding: 0, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          position: 'relative',
          background: 'transparent'
        }}>
          {(() => {
            // Get the image to display
            let displayImage = currentImage;
            const displayCameraLabel = selectedCameraId || 'Camera';
            
            if (selectedStupa.id === 'home' && !currentImage) {
              displayImage = cameraImages[0];
            }
            
            return displayImage && getImageSource(displayImage) ? (
              <>
                <AuthenticatedImage
                  src={getImageSource(displayImage)}
                  alt={`${displayCameraLabel} - ${new Date(displayImage.timestamp).toLocaleString()}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    borderRadius: '8px'
                  }}
                />
                
                {/* Image info overlay */}
                <div style={{
                  position: 'absolute',
                  bottom: '16px',
                  left: '16px',
                  right: '16px',
                  background: 'rgba(0, 0, 0, 0.8)',
                  color: '#f8fafc',
                  padding: '16px',
                  borderRadius: '8px',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(148, 163, 184, 0.3)'
                }}>
                  <Typography style={{
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    marginBottom: '8px'
                  }}>
                    {displayCameraLabel}
                  </Typography>
                  <Typography style={{
                    fontSize: '0.9rem',
                    opacity: 0.9,
                    marginBottom: '4px'
                  }}>
                    {new Date(displayImage.timestamp).toLocaleString()}
                  </Typography>
                  <Typography style={{
                    fontSize: '0.8rem',
                    color: '#94a3b8'
                  }}>
                    Click outside or press ESC to close
                  </Typography>
                </div>
              </>
            ) : null;
          })()}
          
          {/* Close button */}
          <IconButton
            onClick={() => setFullscreenImageOpen(false)}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'rgba(0, 0, 0, 0.8)',
              color: '#f8fafc',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              backdropFilter: 'blur(10px)',
              width: '48px',
              height: '48px'
            }}
          >
            <Close style={{ fontSize: '24px' }} />
          </IconButton>
        </DialogContent>
      </Dialog>

      <footer style={{
        position: 'relative',
        zIndex: 2,
        margin: '32px auto 0',
        padding: '24px 20px 32px',
        maxWidth: 1200,
        textAlign: 'center',
        color: '#94a3b8',
        borderTop: '1px solid rgba(148, 163, 184, 0.2)',
      }}>
        <Typography variant="subtitle2" sx={{ color: '#cbd5e1', mb: 1 }}>
          Confidential &amp; Proprietary
        </Typography>
        <Typography variant="caption" sx={{ lineHeight: 1.6 }}>
          This platform contains proprietary environmental monitoring data, project
          information, images, methodologies, and analytical results developed by
          NILUS SpA and its partners. Access is limited to authorized users.
          Unauthorized use, disclosure, reproduction, or distribution is prohibited
          without prior written consent.
        </Typography>
      </footer>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 0.5; }
            50% { opacity: 1; }
            100% { opacity: 0.5; }
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          /* Custom slider styling */
          input[type="range"]::-webkit-slider-thumb {
            appearance: none;
            -webkit-appearance: none;
            width: 16px; // Reduced from 18px
            height: 16px; // Reduced from 18px
            border-radius: 50%;
            background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
            cursor: pointer;
            border: 2px solid rgba(255, 255, 255, 0.8);
            box-shadow: 0 2px 8px rgba(14, 165, 233, 0.4);
          }
          
          input[type="range"]::-webkit-slider-thumb:hover {
            transform: scale(1.1);
            box-shadow: 0 4px 12px rgba(14, 165, 233, 0.6);
          }
          
          input[type="range"]::-moz-range-thumb {
            width: 16px; // Reduced from 18px
            height: 16px; // Reduced from 18px
            border-radius: 50%;
            background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
            cursor: pointer;
            border: 2px solid rgba(255, 255, 255, 0.8);
            box-shadow: 0 2px 8px rgba(14, 165, 233, 0.4);
          }
          
          input[type="range"]::-moz-range-track {
            height: 5px; // Reduced from 6px to match the input height
            border-radius: 2px; // Reduced from 3px
            background: #64748b;
          }
        `}
      </style>
    </div>
  );
};

const ClientPage = ({ sponsor, preview = false }: ClientPageProps) => {
  const [clientConfig, setClientConfig] = useState<ClientConfig | null>(null);
  const [configurationError, setConfigurationError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    loadClientConfig(preview ? sponsor : undefined)
      .then((config) => {
        if (!active) return;
        if (!preview && config.companyCode !== sponsor) {
          throw new Error('The active organization does not match this dashboard session');
        }
        setClientConfig(config);
      })
      .catch((error) => {
        if (active) {
          setConfigurationError(error instanceof Error ? error.message : 'Dashboard unavailable');
        }
      });
    return () => {
      active = false;
    };
  }, [preview, sponsor]);

  if (configurationError) {
    return (
      <Container maxWidth="sm" sx={{ pt: 16 }}>
        <Typography variant="h4" gutterBottom>
          Dashboard unavailable
        </Typography>
        <Typography color="text.secondary">
          {configurationError}
        </Typography>
      </Container>
    );
  }
  if (!clientConfig) {
    return <DashboardLoadingScreen loadingMessage="Loading company dashboard..." />;
  }

  return (
    <ClientDashboard
      sponsorSlug={clientConfig.companyCode}
      sponsorConfig={{ ...clientConfig, stupaIds: clientConfig.glaciers }}
      preview={preview}
    />
  );
};

export default ClientPage; 